/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { SHIFTIQ_ENABLED } from "../../features";
import { SHIFTIQ_API, getAuthHeaders } from "../../api/client";
import { LoadingPulse } from "../../ui";
import { ganttDisplayRoleKey } from "../../lib/roles";
import { rosterScheduleKey } from "../../employeeMatch";
import { hoursBetweenHhmm, intervalEndHourFloat, timelineBarGeometry } from "../../shiftTimeUtils";

import { employeeDisplayNameFirstWord, slotDraftDisplayName } from "./scheduleHelpers";

export function GanttCompareView({
  store,
  unifiedLocationId = null,
  weekStart,
  lang = "en",
  allStaff = [],
  openTime = "10:00",
  closeTime = "22:00",
  openingHoursByDay = null,
  sharedGanttData = null,
  sharedGanttLoading = false,
  sharedGanttError = "",
  disableInternalFetch = false,
  onRequestRefresh = null,
  ganttScrollTarget = null,
  setGanttScrollTarget = null,
  vsOptimalActive = false,
  setVsOptimalActive = null,
  onBackToSchedule = null,
  forcedDayDate = null,
  forceDayView = false,
  hideHeaderChrome = false,
  initialActiveRole = "all",
  staff = null,
  adminScheduleOverride = false,
  scheduleReadOnly = false,
  loadingAction = null,
  setLoadingAction = null,
  onRequestResetWeek = null,
  onRequestResetAutoFills = null,
}) {
  const ganttStaffList = staff || allStaff || [];
  const toMonday = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d.toISOString().slice(0, 10);
  };

  const [ganttData, setGanttData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ganttView, setGanttView] = useState("week");
  const normalizeGanttActiveRole = (role) =>
    role === "kitchen" || role === "cleaning" ? "boh" : role;
  const [activeRole, setActiveRole] = useState(normalizeGanttActiveRole(initialActiveRole));
  const [applyMsg, setApplyMsg] = useState("");
  const [applyMsgColor, setApplyMsgColor] = useState("#16A34A");
  const [reloadTick, setReloadTick] = useState(0);
  const [dayIndex, setDayIndex] = useState(0);
  const [showFillSlotModal, setShowFillSlotModal] = useState(false);
  const [fillSlotModal, setFillSlotModal] = useState(null);
  const [fillGapLeaveReqs, setFillGapLeaveReqs] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [dragPreview, setDragPreview] = useState(null);
  const [dragHover, setDragHover] = useState({ slotKey: "", mode: "" });
  const [isDraggingDraft, setIsDraggingDraft] = useState(false);
  const [showDragCoachmark, setShowDragCoachmark] = useState(false);
  const [showResetMenu, setShowResetMenu] = useState(false);
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const [showGanttLegend, setShowGanttLegend] = useState(false);
  const [lastDragAudit, setLastDragAudit] = useState(null);
  const [deletingSlot, setDeletingSlot] = useState(null);
  const [deleteErrorSlot, setDeleteErrorSlot] = useState(null);
  const lastDirectionRef = useRef("next");
  const timelineWidthsRef = useRef({});
  const dragStateRef = useRef(null);
  const dragPreviewRef = useRef(null);
  const deleteConfirmTimerRef = useRef(null);
  const deleteErrorTimerRef = useRef(null);
  const pendingScrollRestoreRef = useRef(null);
  const weekDayBlockRefs = useRef({});
  const dayBlockRefs = useRef({});
  const weekScrollContainerRef = useRef(null);

  const roleAbbr = { supervisor: "Sup", service: "Srv", kitchen: "Ktc", cleaning: "Cln", boh: "BOH" };
  const roleOptions = [
    { id: "all", label: "All roles" },
    { id: "supervisor", label: "Supervisor" },
    { id: "service", label: "Service" },
    { id: "boh", label: "Kitchen & Cleaning" },
  ];
  const roleKey = (role) => ganttDisplayRoleKey(role);
  const getRoleAbbr = (role) => roleAbbr[roleKey(role)] || "N/A";
  const statusColors = {
    recommendedBorder: "#94A3B8",
    recommendedBg: "#F8FAFC",
    recommendedText: "#64748B",
    good: "#3B82F6",
    drift: "#F97316",
  };
  const TIMELINE_START = 9;
  const TIMELINE_END = 23;
  const TIMELINE_HOURS = TIMELINE_END - TIMELINE_START;
  const clamp = (val, min, max) => Math.min(Math.max(val, min), max);
  const getInitials = (name) =>
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p.charAt(0).toUpperCase())
      .join("");

  const toHour = (t) => {
    const [h, m] = String(t || "00:00").split(":").map(Number);
    return (h || 0) + (m || 0) / 60;
  };
  const slotHours = (slot) => hoursBetweenHhmm(slot?.start, slot?.end);
  const isSameSlotWindow = (a, b) =>
    roleKey(a?.role) === roleKey(b?.role) &&
    String(a?.start || "") === String(b?.start || "") &&
    String(a?.end || "") === String(b?.end || "");
  const dayOverHours = (day) =>
    Number((day?.extra_slots || []).reduce((sum, slot) => sum + slotHours(slot), 0));
  const deleteSlotKey = (date, role, employeeName, start) => `${date}-${role}-${employeeName}-${start}`;
  const getOpenCloseForDay = (dayName) => {
    const fallback = { open: openTime || "10:00", close: closeTime || "22:00" };
    const range = openingHoursByDay?.[dayName];
    if (!range || range === "CLOSED" || !String(range).includes("-")) return fallback;
    const [open, close] = String(range).split("-");
    return { open: open || fallback.open, close: close || fallback.close };
  };
  const toTimeLabel = (hourFloat) => {
    const totalMins = Math.round(Number(hourFloat || 0) * 60);
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };
  const toClockLabel = (dateObj) => {
    if (!(dateObj instanceof Date)) return "";
    return dateObj.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };
  const triggerGanttRefresh = useCallback(() => {
    if (disableInternalFetch) {
      if (typeof onRequestRefresh === "function") onRequestRefresh();
      return;
    }
    setReloadTick((p) => p + 1);
  }, [disableInternalFetch, onRequestRefresh]);
  const hasDriftAgainstOptimal = (day, slot) => {
    const optimalSlots = (day?.optimal_slots || []).filter((opt) => roleKey(opt.role) === roleKey(slot.role));
    if (!optimalSlots.length) return true;
    const slotStart = toHour(slot.start);
    const slotEnd = toHour(slot.end);
    return !optimalSlots.some((opt) => {
      const optStart = toHour(opt.start);
      const optEnd = toHour(opt.end);
      const overlap = Math.max(0, Math.min(slotEnd, optEnd) - Math.max(slotStart, optStart));
      const withinOneHourTolerance =
        Math.abs(slotStart - optStart) <= 1 && Math.abs(slotEnd - optEnd) <= 1;
      return overlap > 0 || withinOneHourTolerance;
    });
  };
  const applyOptimisticDraftSlotUpdate = ({
    date,
    role,
    employeeName,
    oldStart,
    oldEnd,
    newStart,
    newEnd,
  }) => {
    setGanttData((prev) => {
      if (!prev || !Array.isArray(prev.days)) return prev;
      const nextDays = prev.days.map((day) => {
        if (day?.date !== date || !Array.isArray(day?.draft_slots)) return day;
        let updated = false;
        const normalizedEmployee = normalizeName(employeeName);
        const nextDraftSlots = day.draft_slots.map((slot) => {
          if (updated) return slot;
          if (roleKey(slot.role) !== roleKey(role)) return slot;
          const codeMatch =
            String(slot.employee_code || "").trim() &&
            normalizeName(slot.employee_code) === normalizedEmployee;
          if (!codeMatch && normalizeName(slot.employee_name) !== normalizedEmployee) return slot;
          if (oldStart && String(slot.start || "").slice(0, 5) !== String(oldStart || "").slice(0, 5)) return slot;
          if (oldEnd && String(slot.end || "").slice(0, 5) !== String(oldEnd || "").slice(0, 5)) return slot;
          updated = true;
          const nextSlot = { ...slot, start: newStart, end: newEnd };
          return { ...nextSlot, drifts: hasDriftAgainstOptimal(day, nextSlot) };
        });
        return updated ? { ...day, draft_slots: nextDraftSlots } : day;
      });
      return { ...prev, days: nextDays };
    });
  };
  const snapToHalfHour = (hourFloat) =>
    TIMELINE_START + Math.round((Number(hourFloat || TIMELINE_START) - TIMELINE_START) * 2) / 2;
  const getBarGeometry = (slot) =>
    timelineBarGeometry(slot.start, slot.end, TIMELINE_START, TIMELINE_END);

  useEffect(() => {
    const handler = () => triggerGanttRefresh();
    window.addEventListener("leave-updated", handler);
    return () => window.removeEventListener("leave-updated", handler);
  }, [triggerGanttRefresh]);

  useEffect(() => {
    if (!showResetMenu) return;
    const close = () => setShowResetMenu(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showResetMenu]);

  useEffect(() => {
    if (!showRoleMenu) return;
    const close = () => setShowRoleMenu(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showRoleMenu]);

  useEffect(() => {
    try {
      if (window.localStorage.getItem("gantt_drag_hint_seen_v1") !== "1") {
        setShowDragCoachmark(true);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (disableInternalFetch) {
      setGanttData(sharedGanttData || null);
      setLoading(Boolean(sharedGanttLoading));
      setError(sharedGanttError || "");
      return undefined;
    }
    if (!SHIFTIQ_ENABLED || !store || !weekStart || unifiedLocationId == null) return;
    let cancelled = false;
    setLoading(true);
    setError("");

    fetch(`${SHIFTIQ_API}/optimal-schedule?${`unified_location_id=${encodeURIComponent(unifiedLocationId)}`}&week_start=${weekStart}`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((data) => {
        if (!cancelled) setGanttData(data);
      })
      .catch(() => {
        if (!cancelled) {
          setGanttData(null);
          setError("Failed to load optimal schedule.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [store, unifiedLocationId, weekStart, reloadTick, disableInternalFetch, sharedGanttData, sharedGanttLoading, sharedGanttError]);

  useEffect(() => {
    if (!ganttData || !Array.isArray(ganttData.days) || ganttData.days.length === 0) {
      setDayIndex(0);
      return;
    }
    setDayIndex((prev) => {
      const prevDate = ganttData.days[prev]?.date;
      if (prevDate) {
        const nextIdx = ganttData.days.findIndex((d) => d?.date === prevDate);
        if (nextIdx >= 0) return nextIdx;
      }
      return Math.min(prev, ganttData.days.length - 1);
    });
  }, [ganttData]);

  useEffect(() => {
    if (!forceDayView) return;
    setGanttView("day");
  }, [forceDayView]);

  useEffect(() => {
    if (!initialActiveRole) return;
    setActiveRole(normalizeGanttActiveRole(initialActiveRole));
  }, [initialActiveRole]);

  useEffect(() => {
    if (!forcedDayDate || !Array.isArray(ganttData?.days) || ganttData.days.length === 0) return;
    const nextIdx = ganttData.days.findIndex((d) => String(d?.date || "") === String(forcedDayDate));
    if (nextIdx >= 0) setDayIndex(nextIdx);
  }, [forcedDayDate, ganttData]);

  useEffect(() => {
    if (pendingScrollRestoreRef.current == null) return;
    const scrollY = pendingScrollRestoreRef.current;
    pendingScrollRestoreRef.current = null;
    requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: "instant" }));
  }, [ganttData]);

  const formatFillGapRuleWarnings = (data) => {
    const warnings = Array.isArray(data?.rule_warnings) ? data.rule_warnings : [];
    if (!warnings.length) return "";
    const preview = warnings
      .slice(0, 2)
      .map((w) => String(w?.message || w?.rule || "").trim())
      .filter(Boolean)
      .join(" · ");
    const extra = warnings.length > 2 ? ` (+${warnings.length - 2} more)` : "";
    return preview ? ` — ${preview}${extra}` : "";
  };

  const applyOptimal = () => {
    setLoadingAction("Applying optimal schedule...");
    if (loading) {
      setLoadingAction(null);
      return;
    }
    const visibleDate = getVisibleWeekDayDate();
    fetch(`${SHIFTIQ_API}/optimal-schedule/apply`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ store_id: store, week_start: weekStart }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((data) => {
        const covering = (data?.auto_assigned || []).filter((a) => a?.covering_supervisor);
        const coverNote =
          covering.length > 0
            ? t(
                ` · ${covering.length} service crew covering supervisor`,
                ` · ${covering.length} service σε κάλυψη supervisor`,
              )
            : "";
        setApplyMsg(
          t("Optimal applied — gaps filled", "Εφαρμόστηκε το βέλτιστο — συμπληρώθηκαν κενά") +
            coverNote +
            formatFillGapRuleWarnings(data),
        );
        setApplyMsgColor("#16A34A");
        window.dispatchEvent(
          new CustomEvent("rota-updated", {
            detail: { store_id: store, week_start: weekStart, refreshGantt: true },
          })
        );
        triggerGanttRefresh();
        setTimeout(() => {
          if (ganttView === "week" && visibleDate) {
            const idx = (ganttData?.days || []).findIndex((d) => d.date === visibleDate);
            const dayObj = ganttData?.days?.[idx >= 0 ? idx : 0];
            if (dayObj) scrollToWeekDayBlock(dayObj, idx >= 0 ? idx : 0);
          }
        }, 500);
        setTimeout(() => setApplyMsg(""), 4500);
      })
      .catch(() => {
        setApplyMsg("Failed to apply — try again");
        setApplyMsgColor("#EF4444");
        setTimeout(() => setApplyMsg(""), 2500);
        setTimeout(() => setLoadingAction(null), 8000);
      });
  };

  const summary = ganttData?.summary || {};
  const days = ganttData?.days || [];
  const totalOptimalHours = days.reduce(
    (sum, d) => sum + (d?.optimal_slots || []).reduce((s, slot) => s + Number(slot?.hours || 0), 0),
    0
  );
  const totalGapHours = Number(summary?.total_gap_hours || 0);
  const healthScore = Math.max(
    0,
    Math.round(((totalOptimalHours - totalGapHours) / Math.max(totalOptimalHours, 1)) * 100)
  );
  const healthScoreColor = healthScore >= 85 ? "#16A34A" : healthScore >= 65 ? "#F59E0B" : "#EF4444";
  const hasAnyDraftBars = days.some((d) => (d?.draft_slots || []).length > 0);
  const weekConfidence = useMemo(() => {
    if (!days || days.length === 0) return "high";
    if (days.some((d) => d?.confidence === "low")) return "low";
    if (days.some((d) => d?.confidence === "medium")) return "medium";
    return "high";
  }, [days]);
  const currentDay = days[dayIndex] || null;
  const scrollToWeekDayBlock = (day, idx) => {
    if (ganttView !== "week") return;
    const refKey = String(day?.date || idx);
    const el = dayBlockRefs.current[refKey];
    if (!el || typeof el.scrollIntoView !== "function") return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getVisibleWeekDayDate = () => {
    const container = weekScrollContainerRef?.current;
    if (!container) return null;
    const containerTop = container.getBoundingClientRect().top;
    let closestDate = null;
    let closestDist = Infinity;
    Object.entries(weekDayBlockRefs.current).forEach(([date, el]) => {
      if (!el) return;
      const dist = Math.abs(el.getBoundingClientRect().top - containerTop);
      if (dist < closestDist) {
        closestDist = dist;
        closestDate = date;
      }
    });
    return closestDate;
  };

  useEffect(() => {
    if (!ganttScrollTarget || !Array.isArray(days) || days.length === 0) return;
    if (ganttView === "day") {
      const targetIndex = days.findIndex((d) => String(d?.date || "") === String(ganttScrollTarget));
      if (targetIndex >= 0) setDayIndex(targetIndex);
      if (typeof setGanttScrollTarget === "function") setGanttScrollTarget(null);
      return;
    }
    const timer = setTimeout(() => {
      dayBlockRefs.current[String(ganttScrollTarget)]?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (typeof setGanttScrollTarget === "function") setGanttScrollTarget(null);
    }, 150);
    return () => clearTimeout(timer);
  }, [ganttScrollTarget, ganttView, days, setGanttScrollTarget]);

  const goPrevDay = () => {
    lastDirectionRef.current = "prev";
    setDayIndex((i) => Math.max(0, i - 1));
  };
  const goNextDay = () => {
    lastDirectionRef.current = "next";
    setDayIndex((i) => Math.min(days.length - 1, i + 1));
  };
  const dismissDragCoachmark = () => {
    setShowDragCoachmark(false);
    try {
      window.localStorage.setItem("gantt_drag_hint_seen_v1", "1");
    } catch (_) {}
  };
  const showDragTipsAgain = () => {
    setShowDragCoachmark(true);
    try {
      window.localStorage.removeItem("gantt_drag_hint_seen_v1");
    } catch (_) {}
  };

  const applyDayOptimal = (day) => {
    setLoadingAction("Filling gaps for this day...");
    if (loading) {
      setLoadingAction(null);
      return;
    }
    fetch(`${SHIFTIQ_API}/optimal-schedule/apply`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ store_id: store, week_start: weekStart, date: day.date }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((data) => {
        const covering = (data?.auto_assigned || []).filter((a) => a?.covering_supervisor);
        const coverNote =
          covering.length > 0
            ? t(
                ` · ${covering.length} Srv (sup)`,
                ` · ${covering.length} Srv (sup)`,
              )
            : "";
        setApplyMsg(
          t("Optimal applied — gaps filled", "Εφαρμόστηκε το βέλτιστο — συμπληρώθηκαν κενά") +
            coverNote +
            formatFillGapRuleWarnings(data),
        );
        setApplyMsgColor("#16A34A");
        window.dispatchEvent(
          new CustomEvent("rota-updated", {
            detail: { store_id: store, week_start: weekStart, refreshGantt: true },
          })
        );
        triggerGanttRefresh();
        setTimeout(() => {
          if (ganttView === "week") {
            const idx = (ganttData?.days || []).findIndex((d) => d.date === day.date);
            scrollToWeekDayBlock(day, idx >= 0 ? idx : 0);
          }
        }, 500);
        setTimeout(() => setApplyMsg(""), 4500);
      })
      .catch(() => {
        setApplyMsg("Failed to apply — try again");
        setApplyMsgColor("#EF4444");
        setTimeout(() => setApplyMsg(""), 2500);
        setTimeout(() => setLoadingAction(null), 8000);
      });
  };

  const t = (en, gr) => (lang === "gr" ? gr : en);
  const normalizeName = (name) => String(name || "").trim().toUpperCase();
  const resolveEmployeeCodeForApi = (displayName) => {
    const n = normalizeName(displayName);
    const list = Array.isArray(staff) ? staff : [];
    const hit = list.find((e) => normalizeName(e.name) === n);
    if (hit) return rosterScheduleKey(hit);
    return String(displayName || "").trim();
  };
  const roundGapHours = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;
  const draftSlotMatchesRemove = (s, role, start, end, employeeName) => {
    const n = normalizeName(employeeName);
    const codeMatch = String(s?.employee_code || "").trim() && normalizeName(s.employee_code) === n;
    return (
      (codeMatch || normalizeName(s.employee_name) === n) &&
      roleKey(s.role) === roleKey(role) &&
      String(s.start || "").slice(0, 5) === String(start || "").slice(0, 5) &&
      String(s.end || "").slice(0, 5) === String(end || "").slice(0, 5)
    );
  };
  const applyOptimisticRotaAssign = ({ date, role, start, end, employeeName }) => {
    setGanttData((prev) => {
      if (!prev || !Array.isArray(prev.days)) return prev;
      const dateKey = String(date || "").slice(0, 10);
      const dayIndex = prev.days.findIndex((d) => String(d?.date || "").slice(0, 10) === dateKey);
      if (dayIndex === -1) return prev;
      const prevDay = prev.days[dayIndex];
      const code = resolveEmployeeCodeForApi(employeeName);
      const slotForDrift = { role, start, end, employee_name: employeeName, employee_code: code };
      const newDraftSlot = {
        role,
        start,
        end,
        employee_code: code,
        employee_name: employeeName,
        hours: roundGapHours(hoursBetweenHhmm(start, end)),
        drifts: hasDriftAgainstOptimal(prevDay, slotForDrift),
      };
      const draft_slots = [...(prevDay.draft_slots || []), newDraftSlot];
      let missing_slots = [...(prevDay.missing_slots || [])];
      const missingIndex = missing_slots.findIndex(
        (ms) =>
          roleKey(ms.role) === roleKey(role) &&
          Math.abs(toHour(ms.start) - toHour(start)) <= 1.5 &&
          Math.abs(toHour(ms.end) - toHour(end)) <= 1.5
      );
      if (missingIndex !== -1) {
        const ms = missing_slots[missingIndex];
        const prevRem = Number(ms.remaining_count ?? ms.count ?? 1);
        const remaining = prevRem - 1;
        if (remaining <= 0) {
          missing_slots = missing_slots.filter((_, i) => i !== missingIndex);
        } else {
          const prevGap = Number(ms.gap_hours ?? 0);
          const scale = remaining / prevRem;
          missing_slots = missing_slots.map((s, i) =>
            i === missingIndex
              ? {
                  ...s,
                  remaining_count: remaining,
                  count: remaining,
                  gap_hours: roundGapHours(prevGap * scale),
                }
              : s
          );
        }
      }
      const gap_hours = roundGapHours(missing_slots.reduce((sum, ms) => sum + Number(ms.gap_hours ?? 0), 0));
      const day = {
        ...prevDay,
        draft_slots,
        missing_slots,
        gap_hours,
      };
      const days = prev.days.map((d, i) => (i === dayIndex ? day : d));
      const total_gap_hours = roundGapHours(days.reduce((sum, d) => sum + Number(d.gap_hours ?? 0), 0));
      const days_with_gaps = days.filter((d) => Number(d.gap_hours) > 0).length;
      return {
        ...prev,
        days,
        summary: {
          ...prev.summary,
          total_gap_hours,
          days_with_gaps,
        },
      };
    });
  };
  const applyOptimisticRotaRemove = ({ dayDate, role, start, end, employeeName }) => {
    setGanttData((prev) => {
      if (!prev || !Array.isArray(prev.days)) return prev;
      const dateKey = String(dayDate || "").slice(0, 10);
      const dayIndex = prev.days.findIndex((d) => String(d?.date || "").slice(0, 10) === dateKey);
      if (dayIndex === -1) return prev;
      const prevDay = prev.days[dayIndex];
      const draft_slots = (prevDay.draft_slots || []).filter(
        (s) => !draftSlotMatchesRemove(s, role, start, end, employeeName)
      );
      let missing_slots = [...(prevDay.missing_slots || [])];
      const optMatch = (prevDay.optimal_slots || []).find(
        (o) =>
          roleKey(o.role) === roleKey(role) &&
          Math.abs(toHour(o.start) - toHour(start)) <= 1.5 &&
          Math.abs(toHour(o.end) - toHour(end)) <= 1.5
      );
      if (optMatch) {
        const hoursOne = roundGapHours(hoursBetweenHhmm(optMatch.start, optMatch.end));
        const mIdx = missing_slots.findIndex(
          (ms) =>
            roleKey(ms.role) === roleKey(optMatch.role) &&
            Math.abs(toHour(ms.start) - toHour(optMatch.start)) <= 1.5 &&
            Math.abs(toHour(ms.end) - toHour(optMatch.end)) <= 1.5
        );
        if (mIdx === -1) {
          missing_slots.push({
            role: optMatch.role,
            start: optMatch.start,
            end: optMatch.end,
            gap_hours: hoursOne,
            remaining_count: 1,
            count: 1,
          });
        } else {
          const ms = missing_slots[mIdx];
          const prevRem = Number(ms.remaining_count ?? ms.count ?? 1);
          missing_slots = missing_slots.map((s, i) =>
            i === mIdx
              ? {
                  ...s,
                  remaining_count: prevRem + 1,
                  count: prevRem + 1,
                  gap_hours: roundGapHours(Number(s.gap_hours ?? 0) + hoursOne),
                }
              : s
          );
        }
      }
      const gap_hours = roundGapHours(missing_slots.reduce((sum, ms) => sum + Number(ms.gap_hours ?? 0), 0));
      const day = {
        ...prevDay,
        draft_slots,
        missing_slots,
        gap_hours,
      };
      const days = prev.days.map((d, i) => (i === dayIndex ? day : d));
      const total_gap_hours = roundGapHours(days.reduce((sum, d) => sum + Number(d.gap_hours ?? 0), 0));
      const days_with_gaps = days.filter((d) => Number(d.gap_hours) > 0).length;
      return {
        ...prev,
        days,
        summary: {
          ...prev.summary,
          total_gap_hours,
          days_with_gaps,
        },
      };
    });
  };
  const rosterRaw = Array.isArray(staff) && staff.length > 0 ? staff : allStaff || [];
  const storeFilteredStaff = (rosterRaw || []).filter((e) => {
    const empStore = String(e?.store || e?.store_name || e?.location || "").trim();
    return empStore === store || store === "All Stores" || empStore === "";
  });
  const storeStaff = storeFilteredStaff;
  const getEmployeeWorkDays = (employeeName) => {
    const target = normalizeName(employeeName);
    const staffMatch = storeStaff.find((s) => normalizeName(s?.name) === target);
    return Number(staffMatch?.work_days ?? staffMatch?.days_per_week ?? staffMatch?.workDays ?? 5);
  };
  const roleTitle = (role) => {
    const key = roleKey(role);
    if (key === "supervisor") return t("Supervisor", "Supervisor");
    if (key === "service") return t("Service", "Service");
    if (key === "boh") return t("Kitchen & Cleaning", "Kitchen & Cleaning");
    if (key === "kitchen") return t("Kitchen", "Kitchen");
    if (key === "cleaning") return t("Cleaning", "Cleaning");
    return t("Crew", "Crew");
  };

  const renderConfidenceBadge = (day) => {
    const c = String(day?.confidence || "low").toLowerCase();
    const note = String(day?.confidence_note || "");
    const dotColor =
      c === "high" ? "#16A34A" : c === "medium" ? "#F59E0B" : "var(--text-muted)";
    const label =
      c === "high"
        ? t("High confidence", "Υψηλή εμπιστοσύνη")
        : c === "medium"
          ? t("Guide only", "Μόνο οδηγός")
          : t("Limited data", "Περιορισμένα δεδομένα");
    return (
      <span
        title={note}
        style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: dotColor,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 9, color: dotColor, fontWeight: 600 }}>{label}</span>
      </span>
    );
  };

  const renderExceptionalDayBadge = (day) => {
    const exc = day?.exceptional_day;
    if (!exc || typeof exc !== "object") return null;
    const rawLabel = String(exc.label || "").trim();
    const short =
      rawLabel.length > 42 ? `${rawLabel.slice(0, 40)}…` : rawLabel || "Event";
    const m = Number(exc.multiplier ?? 1);
    const tip = t(
      `Revenue forecast adjusted by ${m}x for this event`,
      `Πρόβλεψη εσόδων προσαρμοσμένη κατά ${m}x για αυτό το γεγονός`,
    );
    return (
      <span
        title={tip}
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginLeft: 4,
          background: "#FEF3C7",
          color: "#92400E",
          fontSize: 9,
          fontWeight: 800,
          borderRadius: 4,
          padding: "1px 6px",
          maxWidth: "100%",
        }}
      >
        {`⚡ ${short}`}
      </span>
    );
  };

  const resetOptimalWeek = () => {
    setLoadingAction("Resetting auto-filled shifts...");
    if (loading) {
      setLoadingAction(null);
      return;
    }
    const visibleDate = getVisibleWeekDayDate();
    fetch(`${SHIFTIQ_API}/optimal-schedule/reset`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        store_id: store,
        week_start: toMonday(weekStart),
        force: false,
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((data) => {
        const n = Number(data?.removed ?? 0);
        setApplyMsgColor(n > 0 ? "#16A34A" : "var(--text-muted)");
        setApplyMsg(
          n > 0
            ? t(`Removed ${n} auto-filled slot${n === 1 ? "" : "s"}`, `Αφαιρέθηκαν ${n} αυτόματες βάρδιες`)
            : t("No auto-filled slots to remove", "Δεν υπάρχουν αυτόματες βάρδιες προς αφαίρεση"),
        );
        window.dispatchEvent(
          new CustomEvent("rota-updated", {
            detail: { store_id: store, week_start: toMonday(weekStart), refreshGantt: true },
          })
        );
        triggerGanttRefresh();
        setTimeout(() => {
          if (ganttView === "week" && visibleDate) {
            const idx = (ganttData?.days || []).findIndex((d) => d.date === visibleDate);
            const dayObj = ganttData?.days?.[idx >= 0 ? idx : 0];
            if (dayObj) scrollToWeekDayBlock(dayObj, idx >= 0 ? idx : 0);
          }
        }, 500);
        setTimeout(() => setApplyMsg(""), 3500);
      })
      .catch(() => {
        setApplyMsg(t("Reset failed — try again", "Η επαναφορά απέτυχε"));
        setApplyMsgColor("#EF4444");
        setTimeout(() => setApplyMsg(""), 3500);
        setTimeout(() => setLoadingAction(null), 8000);
      });
  };

  const clearWeek = () => {
    setLoadingAction("Clearing week...");
    if (loading) {
      setLoadingAction(null);
      return;
    }
    if (
      !window.confirm(
        t(
          "Clear ALL shifts for the entire week? This cannot be undone.",
          "Διαγραφή ΟΛΩΝ των βαρδιών για ολόκληρη την εβδομάδα; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.",
        ),
      )
    ) {
      setLoadingAction(null);
      return;
    }
    const visibleDate = getVisibleWeekDayDate();
    fetch(`${SHIFTIQ_API}/optimal-schedule/reset`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        store_id: store,
        week_start: toMonday(weekStart),
        force: true,
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((data) => {
        const n = Number(data?.removed ?? 0);
        setApplyMsgColor(n > 0 ? "#16A34A" : "var(--text-muted)");
        setApplyMsg(
          n > 0
            ? t(`Cleared ${n} shift${n === 1 ? "" : "s"} from the week`, `Αφαιρέθηκαν ${n} βάρδιες από την εβδομάδα`)
            : t("No shifts to clear", "Δεν υπάρχουν βάρδιες προς αφαίρεση"),
        );
        window.dispatchEvent(
          new CustomEvent("rota-updated", {
            detail: { store_id: store, week_start: toMonday(weekStart), refreshGantt: true },
          })
        );
        triggerGanttRefresh();
        setTimeout(() => {
          if (ganttView === "week" && visibleDate) {
            const idx = (ganttData?.days || []).findIndex((d) => d.date === visibleDate);
            const dayObj = ganttData?.days?.[idx >= 0 ? idx : 0];
            if (dayObj) scrollToWeekDayBlock(dayObj, idx >= 0 ? idx : 0);
          }
        }, 500);
        setTimeout(() => setApplyMsg(""), 3500);
      })
      .catch(() => {
        setApplyMsg(t("Clear failed — try again", "Η εκκαθάριση απέτυχε"));
        setApplyMsgColor("#EF4444");
        setTimeout(() => setApplyMsg(""), 3500);
        setTimeout(() => setLoadingAction(null), 8000);
      });
  };

  const resetOptimalDay = (day) => {
    setLoadingAction("Clearing day...");
    if (!day?.date || loading) {
      setLoadingAction(null);
      return;
    }
    fetch(`${SHIFTIQ_API}/optimal-schedule/reset`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        store_id: store,
        week_start: toMonday(weekStart),
        date: day.date,
        force: true,
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((data) => {
        const n = Number(data?.removed ?? 0);
        setApplyMsgColor(n > 0 ? "#16A34A" : "var(--text-muted)");
        setApplyMsg(
          n > 0
            ? t(`Cleared ${n} shift${n === 1 ? "" : "s"} on this day`, `Αφαιρέθηκαν ${n} βάρδιες για αυτή την ημέρα`)
            : t("No shifts to clear on this day", "Δεν υπάρχουν βάρδιες προς αφαίρεση για αυτή την ημέρα"),
        );
        window.dispatchEvent(
          new CustomEvent("rota-updated", {
            detail: { store_id: store, week_start: toMonday(weekStart), refreshGantt: true },
          })
        );
        triggerGanttRefresh();
        setTimeout(() => {
          if (ganttView === "week") {
            const idx = (ganttData?.days || []).findIndex((d) => d.date === day.date);
            scrollToWeekDayBlock(day, idx >= 0 ? idx : 0);
          }
        }, 500);
        setTimeout(() => setApplyMsg(""), 3500);
      })
      .catch(() => {
        setApplyMsg(t("Reset failed — try again", "Η επαναφορά απέτυχε"));
        setApplyMsgColor("#EF4444");
        setTimeout(() => setApplyMsg(""), 3500);
        setTimeout(() => setLoadingAction(null), 8000);
      });
  };

  const toTitleCaseName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  const draftEmployees = (ganttData?.days || [])
    .flatMap((d) => d?.draft_slots || [])
    .filter((s) => {
      const name = String(s?.employee_name || "").trim();
      return Boolean(name) && name !== "__gap__" && !name.toUpperCase().startsWith("OPTIMAL_");
    })
    .map((s) => {
      const first = String(s.employee_name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)[0] || "";
      const staffMatch = storeFilteredStaff.find((e) =>
        String(e.name || e.employee_name || "")
          .toLowerCase()
          .includes(first.toLowerCase())
      );
      return {
        ...s,
        employee_name: toTitleCaseName(s.employee_name),
        role: s.role,
        work_days: staffMatch?.work_days ?? staffMatch?.workDays ?? 5,
      };
    });
  const fromStaffFallback = storeFilteredStaff
    .map((e) => {
      const empStore = String(e?.store || e?.store_name || e?.location || "").trim();
      return {
        employee_name: toTitleCaseName(e.name || e.employee_name || ""),
        role: e.group || e.role || "service",
        work_days: e.work_days ?? e.workDays ?? 5,
        storeUnknown: empStore === "",
      };
    })
    .filter((e) => String(e.employee_name || "").trim().length > 0);
  const rosterByKey = new Map();
  for (const e of fromStaffFallback) {
    const k = String(e.employee_name || "").toLowerCase().trim();
    if (k) rosterByKey.set(k, { ...e });
  }
  for (const d of draftEmployees) {
    const k = String(d.employee_name || "").toLowerCase().trim();
    if (!k) continue;
    const prev = rosterByKey.get(k);
    if (prev) {
      rosterByKey.set(k, {
        ...prev,
        ...d,
        role: d.role ?? prev.role,
        employee_name: d.employee_name || prev.employee_name,
        work_days: prev.work_days ?? d.work_days,
        storeUnknown: Boolean(prev.storeUnknown),
      });
    } else {
      rosterByKey.set(k, d);
    }
  }
  const uniqueEmployees = Array.from(rosterByKey.values()).filter(
    (e) => String(e.employee_name || "").trim().length > 0
  );
  const selectedRoleTitle = fillSlotModal?.role ? roleTitle(fillSlotModal.role) : "";
  const employeeOptions = uniqueEmployees
    .reduce((acc, slot) => {
      const name = String(slot?.employee_name || "").trim();
      if (!name) return acc;
      const existing = acc.get(name) || { name, titles: new Set(), storeUnknown: false };
      if (slot?.storeUnknown) existing.storeUnknown = true;
      const title = roleTitle(slot?.role);
      if (title) existing.titles.add(title);
      acc.set(name, existing);
      return acc;
    }, new Map());
  const employeeSelectOptions = Array.from(employeeOptions.values()).map((entry) => {
    const titles = Array.from(entry.titles || []);
    const workDays = getEmployeeWorkDays(entry.name);
    const orderedTitles =
      selectedRoleTitle && titles.includes(selectedRoleTitle)
        ? [selectedRoleTitle, ...titles.filter((x) => x !== selectedRoleTitle)]
        : titles;
    const unknownSuf = entry.storeUnknown ? t(" (store unknown)", " (άγνωστο κατάστημα)") : "";
    const nameWithNote = `${entry.name}${unknownSuf}`;
    return {
      name: entry.name,
      label: orderedTitles.length ? `${nameWithNote} · ${orderedTitles.join(" / ")}` : nameWithNote,
      workDays,
      storeUnknown: Boolean(entry.storeUnknown),
    };
  });
  useEffect(() => {
    if (!fillSlotModal?.date || !store) {
      setFillGapLeaveReqs([]);
      return undefined;
    }
    let cancelled = false;
    if (!store || unifiedLocationId == null) return undefined;
    fetch(
      `${SHIFTIQ_API}/leave-requests?unified_location_id=${encodeURIComponent(unifiedLocationId)}&status=approved`,
      { headers: getAuthHeaders() },
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (!cancelled) setFillGapLeaveReqs(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setFillGapLeaveReqs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fillSlotModal?.date, store, unifiedLocationId]);

  const fillModalDayData = fillSlotModal?.date
    ? (ganttData?.days || []).find(
        (d) => String(d?.date || "").slice(0, 10) === String(fillSlotModal.date || "").slice(0, 10)
      )
    : null;
  const fillGapSlotDate = String(fillSlotModal?.date || "").slice(0, 10);
  const fillGapApprovedLeaveNames = new Set(
    (fillGapLeaveReqs || [])
      .filter((lr) => {
        if (String(lr?.status || "").toLowerCase() !== "approved") return false;
        return String(lr?.date || "").slice(0, 10) === fillGapSlotDate;
      })
      .map((lr) => String(lr?.employee_name || "").trim().toUpperCase())
      .filter(Boolean),
  );
  const isOnApprovedLeave = (displayName) =>
    fillGapApprovedLeaveNames.has(String(displayName || "").trim().toUpperCase());
  const alreadyScheduledToday = new Set(
    (fillModalDayData?.draft_slots || []).map((s) =>
      String(s?.employee_name || "").toLowerCase().trim()
    )
  );
  const toMinutesForCoverage = (timeStr) => {
    if (!timeStr) return null;
    const parts = String(timeStr).split(":");
    const h = Number(parts[0]);
    const m = Number(parts[1] ?? 0);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };
  let fillGapAvailableOptionSuffix = "";
  if (fillSlotModal?.role && roleKey(fillSlotModal.role) !== "supervisor") {
    const fillGapRoleKey = roleKey(fillSlotModal.role);
    const criticalRole = fillGapRoleKey === "service" || fillGapRoleKey === "boh";
    if (criticalRole) {
      const storeOpenMins = toMinutesForCoverage(openTime || "10:00");
      const storeCloseMins = toMinutesForCoverage(closeTime || "22:00");
      const slotStartMins = toMinutesForCoverage(fillSlotModal.start);
      const slotEndMins = toMinutesForCoverage(fillSlotModal.end);
      if (
        storeOpenMins != null &&
        storeCloseMins != null &&
        slotStartMins != null &&
        slotEndMins != null
      ) {
        const coversOpening = slotStartMins <= storeOpenMins + 30;
        const coversClosing = slotEndMins >= storeCloseMins - 30;
        const dayData = fillModalDayData;
        const openingAlreadyCovered = (dayData?.draft_slots || []).some((s) => {
          const sm = toMinutesForCoverage(s.start);
          return roleKey(s.role) === fillGapRoleKey && sm != null && sm <= storeOpenMins + 30;
        });
        const closingAlreadyCovered = (dayData?.draft_slots || []).some((s) => {
          const em = toMinutesForCoverage(s.end);
          return roleKey(s.role) === fillGapRoleKey && em != null && em >= storeCloseMins - 30;
        });
        const needsOpenBadge = coversOpening && !openingAlreadyCovered;
        const needsCloseBadge = coversClosing && !closingAlreadyCovered;
        if (needsOpenBadge && needsCloseBadge) fillGapAvailableOptionSuffix = " · Opens + closes";
        else if (needsOpenBadge) fillGapAvailableOptionSuffix = " · Opens store";
        else if (needsCloseBadge) fillGapAvailableOptionSuffix = " · Closes store";
      }
    }
  }
  const byFirstName = (a, b) =>
    employeeDisplayNameFirstWord(a.name).localeCompare(employeeDisplayNameFirstWord(b.name)) ||
    a.name.localeCompare(b.name);
  const getScheduledDaysCount = (employeeName) => {
    const target = String(employeeName || "").toLowerCase().trim();
    return (ganttData?.days || []).filter((day) =>
      (day?.draft_slots || []).some(
        (s) => String(s?.employee_name || "").toLowerCase().trim() === target
      )
    ).length;
  };
  const getContractedDays = (employeeName) => {
    const target = String(employeeName || "").toLowerCase().trim();
    const match = (storeStaff || []).find(
      (s) => String(s?.name || s?.employee_name || "").toLowerCase().trim() === target
    );
    const raw = Number(match?.work_days ?? match?.workDays ?? 5);
    return raw > 0 ? raw : 5;
  };
  const hasRestViolation = (employeeName, newDate, newStart, newEnd) => {
    const toMinutes = (timeStr) => {
      if (!timeStr) return null;
      const parts = String(timeStr).split(":");
      const h = Number(parts[0]);
      const m = Number(parts[1] ?? 0);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return h * 60 + m;
    };
    const newStartMins = toMinutes(newStart);
    const newEndMins = toMinutes(newEnd);
    if (newStartMins == null || newEndMins == null || !newDate) return { violated: false };
    const newDateObj = (() => {
      const parts = String(newDate).split(/[-/T]/).map(Number).filter(Number.isFinite);
      if (parts.length < 3) return NaN;
      return new Date(parts[0], parts[1] - 1, parts[2]);
    })();
    if (!Number.isFinite(newDateObj?.getTime?.())) return { violated: false };

    const daysArr = ganttData?.days || [];
    for (const day of daysArr) {
      const daySlots = (day?.draft_slots || []).filter(
        (s) =>
          String(s?.employee_name || "").toLowerCase().trim() === String(employeeName || "").toLowerCase().trim() &&
          s.start &&
          s.end
      );
      for (const slot of daySlots) {
        const slotDateParts = String(day.date || "").split(/[-/T]/).map(Number).filter(Number.isFinite);
        if (slotDateParts.length < 3) continue;
        const slotDate = new Date(slotDateParts[0], slotDateParts[1] - 1, slotDateParts[2]);
        const slotStartMins = toMinutes(slot.start);
        const slotEndMins = toMinutes(slot.end);
        if (slotStartMins == null || slotEndMins == null) continue;
        const dayDiffMs = newDateObj - slotDate;
        const dayDiff = Math.round(dayDiffMs / (1000 * 60 * 60 * 24));

        if (dayDiff === 1) {
          let gap = newStartMins - slotEndMins;
          if (gap < 0) gap += 1440;
          if (gap < 660) return { violated: true, gap, prevEnd: slot.end, prevDate: day.date };
        } else if (dayDiff === -1) {
          let gap = slotStartMins - newEndMins;
          if (gap < 0) gap += 1440;
          if (gap < 660) return { violated: true, gap, nextStart: slot.start, nextDate: day.date };
        } else if (dayDiff === 0) {
          // overlap handled elsewhere — not counted as rest gap here
        }
      }
    }
    return { violated: false };
  };
  const withScheduleMeta = employeeSelectOptions.map((opt) => {
    const scheduledDays = getScheduledDaysCount(opt.name);
    const contractedDays = getContractedDays(opt.name);
    const atMaxDays = scheduledDays >= contractedDays;
    let restViolation = { violated: false };
    if (
      fillSlotModal?.date &&
      fillSlotModal?.start &&
      fillSlotModal?.end &&
      !alreadyScheduledToday.has(String(opt.name || "").toLowerCase().trim()) &&
      (!atMaxDays || adminScheduleOverride)
    ) {
      restViolation = hasRestViolation(opt.name, fillSlotModal.date, fillSlotModal.start, fillSlotModal.end);
    }
    return { ...opt, scheduledDays, contractedDays, atMaxDays, restViolation };
  });
  const getEmployeeSourceRole = (displayName) => {
    const n = String(displayName || "").toLowerCase().trim();
    const row = uniqueEmployees.find((e) => String(e.employee_name || "").toLowerCase().trim() === n);
    return row?.role || "";
  };
  const gapRole = fillSlotModal?.role;
  const gapRoleKeyNorm = gapRole ? roleKey(gapRole) : "";
  const roleMatchEmp = (empRole) => {
    const r = String(empRole || "")
      .toLowerCase()
      .trim();
    if (!gapRoleKeyNorm) return true;
    if (gapRoleKeyNorm === "service") return r.includes("service");
    if (gapRoleKeyNorm === "supervisor") return r.includes("supervisor") || r.includes("manager");
    if (gapRoleKeyNorm === "boh") return r.includes("kitchen") || r.includes("cleaning") || r.includes("clean");
    return true;
  };
  const availableEmployeeOptions = withScheduleMeta.filter((opt) => {
    const normalized = String(opt.name || "").toLowerCase().trim();
    if (alreadyScheduledToday.has(normalized)) return false;
    if (isOnApprovedLeave(opt.name)) return false;
    if (adminScheduleOverride) return true;
    if (opt.atMaxDays) return false;
    if (opt.restViolation?.violated) return false;
    return true;
  });
  const onLeaveEmployeeOptions = withScheduleMeta
    .filter((opt) => isOnApprovedLeave(opt.name))
    .sort(byFirstName);
  const primaryAvailable = availableEmployeeOptions
    .filter((e) => roleMatchEmp(getEmployeeSourceRole(e.name)))
    .sort(byFirstName);
  const secondaryAvailable = availableEmployeeOptions
    .filter((e) => !roleMatchEmp(getEmployeeSourceRole(e.name)))
    .sort(byFirstName);
  const alreadyScheduledEmployeeOptions = withScheduleMeta
    .filter((opt) => alreadyScheduledToday.has(String(opt.name || "").toLowerCase().trim()))
    .sort(byFirstName);
  const restViolationEmployeeOptions = withScheduleMeta
    .filter((opt) => {
      const normalized = String(opt.name || "").toLowerCase().trim();
      if (alreadyScheduledToday.has(normalized)) return false;
      if (opt.atMaxDays) return false;
      return Boolean(opt.restViolation?.violated);
    })
    .sort(byFirstName);
  const maxDaysReachedEmployeeOptions = withScheduleMeta
    .filter((opt) => {
      const normalized = String(opt.name || "").toLowerCase().trim();
      const isWorkingToday = alreadyScheduledToday.has(normalized);
      return !isWorkingToday && opt.atMaxDays;
    })
    .sort(byFirstName);
  const gapRoleTitleForSummary = fillSlotModal?.role ? roleTitle(fillSlotModal.role) : "Crew";
  const fillGapAvailabilitySummary = [
    primaryAvailable.length + secondaryAvailable.length > 0
      ? `${primaryAvailable.length} ${gapRoleTitleForSummary} crew available · ${secondaryAvailable.length} other roles`
      : null,
    alreadyScheduledEmployeeOptions.length > 0 ? `${alreadyScheduledEmployeeOptions.length} working today` : null,
    onLeaveEmployeeOptions.length > 0 ? `${onLeaveEmployeeOptions.length} on approved leave` : null,
    !adminScheduleOverride && restViolationEmployeeOptions.length > 0
      ? `${restViolationEmployeeOptions.length} rest violation`
      : null,
    !adminScheduleOverride && maxDaysReachedEmployeeOptions.length > 0
      ? `${maxDaysReachedEmployeeOptions.length} at max days`
      : null,
    adminScheduleOverride
      ? t(
          "Gap fill allows contracted days & rest overrides (confirm on assign)",
          "Η συμπλήρωση επιτρέπει υπέρβαση ημερών συμβολαίου & ανάπαυσης (επιβεβαίωση στην ανάθεση)",
        )
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const fillGapCanAssign =
    Boolean(selectedEmployee) &&
    (primaryAvailable.some((opt) => opt.name === selectedEmployee) ||
      secondaryAvailable.some((opt) => opt.name === selectedEmployee) ||
      (adminScheduleOverride &&
        selectedEmployee &&
        !alreadyScheduledToday.has(String(selectedEmployee).toLowerCase().trim()) &&
        withScheduleMeta.some((opt) => opt.name === selectedEmployee)));
  let fillGapBlockWarning = null;
  if (selectedEmployee && !fillGapCanAssign) {
    if (onLeaveEmployeeOptions.some((o) => o.name === selectedEmployee)) {
      fillGapBlockWarning = t(
        "Cannot assign: on approved leave",
        "Αδύνατη ανάθεση: εγκεκριμένη άδεια",
      );
    } else if (alreadyScheduledEmployeeOptions.some((o) => o.name === selectedEmployee)) {
      fillGapBlockWarning = t("Cannot assign: already working today", "Αδύνατη ανάθεση: ήδη εργάζεται σήμερα");
    } else if (restViolationEmployeeOptions.some((o) => o.name === selectedEmployee)) {
      fillGapBlockWarning = t(
        "Cannot assign: less than 11 hours rest since previous shift",
        "Αδύνατη ανάθεση: λιγότερο από 11 ώρες διαδοχική ανάπαυσης από προηγούμενη βάρδια",
      );
    } else if (maxDaysReachedEmployeeOptions.some((o) => o.name === selectedEmployee)) {
      fillGapBlockWarning = t(
        "Cannot assign: already at maximum contracted days",
        "Αδύνατη ανάθεση: έχει ήδη φτάσει τις μέρες όπως το συμβόλαιο",
      );
    }
  }

  const handleFillSlot = (date, role, start, end, options = {}) => {
    if (scheduleReadOnly) return;
    const employeeName = String(options?.employeeName || "").trim();
    if (employeeName) {
      fetch(`${SHIFTIQ_API}/rota/assign`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          date,
          role,
          shift_start: start,
          shift_end: end,
          employee_code: resolveEmployeeCodeForApi(employeeName),
          store_id: store,
          week_start: toMonday(weekStart),
        }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
        .then(() => {
          window.dispatchEvent(
            new CustomEvent("rota-updated", {
              detail: {
                store_id: store,
                week_start: weekStart,
                refreshGantt: options?.optimistic ? false : true,
              },
            })
          );
          setLastDragAudit({
            employeeName,
            start,
            end,
            at: new Date(),
          });
          setApplyMsg(t("Shift updated", "Η βάρδια ενημερώθηκε"));
          setApplyMsgColor("#16A34A");
          setTimeout(() => setApplyMsg(""), 1800);
          if (!options?.optimistic) {
            applyOptimisticRotaAssign({ date, role, start, end, employeeName });
          }
        })
        .catch(() => {
          if (options?.optimistic && options?.ganttDataSnapshot) {
            setGanttData(options.ganttDataSnapshot);
          }
          setApplyMsg(t("Shift update failed", "Η ενημέρωση βάρδιας απέτυχε"));
          setApplyMsgColor("#EF4444");
          setTimeout(() => setApplyMsg(""), 2800);
        });
      return;
    }
    setFillSlotModal({ date, role, start, end });
    setSelectedEmployee("");
    setShowFillSlotModal(true);
  };

  useEffect(() => {
    if (!isDraggingDraft) return undefined;
    const onWindowMouseMove = (event) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const pxPerHour = dragState.containerWidth / TIMELINE_HOURS;
      if (!pxPerHour || !Number.isFinite(pxPerHour)) return;
      const deltaHoursRaw = (event.clientX - dragState.originX) / pxPerHour;
      const deltaHours = Math.round(deltaHoursRaw * 2) / 2;
      let nextStart = dragState.startHour;
      let nextEnd = dragState.endHour;
      const duration = dragState.endHour - dragState.startHour;
      if (dragState.mode === "move") {
        nextStart = dragState.startHour + deltaHours;
        nextEnd = dragState.endHour + deltaHours;
        if (nextStart < TIMELINE_START) {
          nextStart = TIMELINE_START;
          nextEnd = TIMELINE_START + duration;
        }
        if (nextEnd > TIMELINE_END) {
          nextEnd = TIMELINE_END;
          nextStart = TIMELINE_END - duration;
        }
      } else if (dragState.mode === "resize-left") {
        const minStart = TIMELINE_START;
        const maxStart = dragState.endHour - 0.5;
        nextStart = clamp(dragState.startHour + deltaHours, minStart, maxStart);
      } else if (dragState.mode === "resize-right") {
        const minEnd = dragState.startHour + 0.5;
        const maxEnd = TIMELINE_END;
        nextEnd = clamp(dragState.endHour + deltaHours, minEnd, maxEnd);
      }
      const snappedStart = snapToHalfHour(nextStart);
      const snappedEnd = snapToHalfHour(nextEnd);
      if (snappedEnd - snappedStart < 0.5) return;
      const previewNext = {
        slotKey: dragState.slotKey,
        dayDate: dragState.dayDate,
        role: dragState.role,
        startHour: snappedStart,
        endHour: snappedEnd,
      };
      dragPreviewRef.current = previewNext;
      setDragPreview(previewNext);
    };
    const onWindowMouseUp = () => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const preview =
        dragPreviewRef.current && dragPreviewRef.current.slotKey === dragState.slotKey
          ? dragPreviewRef.current
          : null;
      if (preview) {
        const movedBy =
          Math.abs(preview.startHour - dragState.startHour) + Math.abs(preview.endHour - dragState.endHour);
        if (movedBy > 0.25) {
          const oldStartLabel = toTimeLabel(dragState.startHour);
          const oldEndLabel = toTimeLabel(dragState.endHour);
          const newStartLabel = toTimeLabel(preview.startHour);
          const newEndLabel = toTimeLabel(preview.endHour);
          const ganttDataSnapshot = ganttData;
          applyOptimisticDraftSlotUpdate({
            date: dragState.dayDate,
            role: dragState.role,
            employeeName: dragState.employeeName,
            oldStart: oldStartLabel,
            oldEnd: oldEndLabel,
            newStart: newStartLabel,
            newEnd: newEndLabel,
          });
          handleFillSlot(dragState.dayDate, dragState.role, newStartLabel, newEndLabel, {
            employeeName: dragState.employeeName,
            optimistic: true,
            oldStart: oldStartLabel,
            oldEnd: oldEndLabel,
            ganttDataSnapshot,
          });
        }
      }
      dragStateRef.current = null;
      dragPreviewRef.current = null;
      setDragPreview(null);
      setDragHover({ slotKey: "", mode: "" });
      setIsDraggingDraft(false);
    };
    window.addEventListener("mousemove", onWindowMouseMove);
    window.addEventListener("mouseup", onWindowMouseUp);
    return () => {
      window.removeEventListener("mousemove", onWindowMouseMove);
      window.removeEventListener("mouseup", onWindowMouseUp);
    };
  }, [isDraggingDraft, dragPreview, ganttData, TIMELINE_HOURS, TIMELINE_END, TIMELINE_START]);

  const closeFillModal = () => {
    setShowFillSlotModal(false);
    setFillSlotModal(null);
    setSelectedEmployee("");
  };

  const assignFillSlot = () => {
    if (!fillSlotModal || !selectedEmployee || !fillGapCanAssign) return;
    fetch(`${SHIFTIQ_API}/rota/assign`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        date: fillSlotModal.date,
        role: fillSlotModal.role,
        shift_start: fillSlotModal.start,
        shift_end: fillSlotModal.end,
        employee_code: resolveEmployeeCodeForApi(selectedEmployee),
        store_id: store,
        week_start: toMonday(weekStart),
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then(() => {
        applyOptimisticRotaAssign({
          date: fillSlotModal.date,
          role: fillSlotModal.role,
          start: fillSlotModal.start,
          end: fillSlotModal.end,
          employeeName: selectedEmployee,
        });
        closeFillModal();
        window.dispatchEvent(
          new CustomEvent("rota-updated", {
            detail: { store_id: store, week_start: weekStart, refreshGantt: true },
          })
        );
      })
      .catch(() => {
        closeFillModal();
      });
  };

  const handleDeleteSlot = (dayDate, role, start, end, employeeName) => {
    if (scheduleReadOnly) return;
    const slotKey = deleteSlotKey(dayDate, role, employeeName, start);
    if (deletingSlot === slotKey) return;
    setDeleteErrorSlot(null);
    setDeletingSlot(slotKey);
    if (deleteConfirmTimerRef.current) clearTimeout(deleteConfirmTimerRef.current);
    deleteConfirmTimerRef.current = setTimeout(() => setDeletingSlot(null), 3000);
  };

  const confirmDeleteSlot = (dayDate, role, start, end, employeeName) => {
    const slotKey = deleteSlotKey(dayDate, role, employeeName, start);
    if (deleteConfirmTimerRef.current) clearTimeout(deleteConfirmTimerRef.current);
    fetch(`${SHIFTIQ_API}/rota/remove`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        store_id: store,
        week_start: toMonday(weekStart),
        date: dayDate,
        role,
        shift_start: start,
        shift_end: end,
        employee_code: resolveEmployeeCodeForApi(employeeName),
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then(() => {
        setDeletingSlot(null);
        setDeleteErrorSlot(null);
        applyOptimisticRotaRemove({ dayDate, role, start, end, employeeName });
        window.dispatchEvent(
          new CustomEvent("rota-updated", {
            detail: { store_id: store, week_start: weekStart, refreshGantt: true },
          })
        );
      })
      .catch(() => {
        setDeletingSlot(null);
        setDeleteErrorSlot(slotKey);
        if (deleteErrorTimerRef.current) clearTimeout(deleteErrorTimerRef.current);
        deleteErrorTimerRef.current = setTimeout(() => setDeleteErrorSlot(null), 2000);
      });
  };

  useEffect(() => {
    return () => {
      if (deleteConfirmTimerRef.current) clearTimeout(deleteConfirmTimerRef.current);
      if (deleteErrorTimerRef.current) clearTimeout(deleteErrorTimerRef.current);
    };
  }, []);

  const renderDayTimeline = (day, dayKey) => {
    const assignLanes = (slots) => {
      const lanes = [];
      return slots.map((slot) => {
        const startH = toHour(slot.start);
        const endH = intervalEndHourFloat(slot.start, slot.end);
        let laneIndex = lanes.findIndex(
          (lane) =>
            !lane.some((s) => {
              const sStart = toHour(s.start);
              const sEnd = intervalEndHourFloat(s.start, s.end);
              return sStart < endH && sEnd > startH;
            })
        );
        if (laneIndex === -1) {
          laneIndex = lanes.length;
          lanes.push([]);
        }
        lanes[laneIndex].push(slot);
        return { ...slot, laneIndex };
      });
    };
    const mergeSlotsByWindow = (slots) => {
      const mergedOptimal = [];
      const seen = new Map();
      for (const slot of slots) {
        const key = `${String(slot.role || "")}${String(slot.start || "")}${String(slot.end || "")}`;
        if (seen.has(key)) {
          const existing = seen.get(key);
          existing.count = (existing.count || 1) + (slot.count || 1);
        } else {
          const merged = { ...slot, count: slot.count != null ? Number(slot.count) : 1 };
          seen.set(key, merged);
          mergedOptimal.push(merged);
        }
      }
      return mergedOptimal;
    };
    const mergeMissingSlotsByWindow = (slots) => {
      const merged = [];
      const seen = new Map();
      for (const slot of slots) {
        const key = `${String(slot.role || "")}${String(slot.start || "")}${String(slot.end || "")}`;
        const n =
          slot.remaining_count != null && slot.remaining_count !== ""
            ? Number(slot.remaining_count)
            : slot.count != null
              ? Number(slot.count)
              : 1;
        if (seen.has(key)) {
          const existing = seen.get(key);
          const prev =
            existing.remaining_count != null && existing.remaining_count !== ""
              ? Number(existing.remaining_count)
              : existing.count || 1;
          existing.remaining_count = prev + n;
          existing.count = existing.remaining_count;
          const addGh = Number(slot.gap_hours || 0);
          existing.gap_hours = Number(existing.gap_hours || 0) + (Number.isFinite(addGh) ? addGh : 0);
        } else {
          const m = { ...slot, remaining_count: n, count: n };
          seen.set(key, m);
          merged.push(m);
        }
      }
      return merged;
    };
    const roles = ["supervisor", "service", "boh"];
    return (
      <div
        key={dayKey}
        style={{
          marginBottom: 0,
          borderBottom: "1px solid var(--border)",
          paddingBottom: 12,
          paddingTop: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", width: 36, flexShrink: 0 }}>{day.day_name}</div>
        </div>

        {roles.map((roleName) => {
          if (activeRole !== "all" && activeRole !== roleName) return null;

          const roleOptimal = (day.optimal_slots || []).filter((s) => roleKey(s.role) === roleName);
          const mergedOptimal = mergeSlotsByWindow(roleOptimal);
          const filteredDraftSlots = (day.draft_slots || []).filter((s) => roleKey(s.role) === roleName);
          const seen = new Set();
          const dedupedDraftSlots = filteredDraftSlots.filter((s) => {
            const key = [
              String(s.employee_name || "").trim().toLowerCase(),
              String(s.start || "").slice(0, 5),
              String(s.end || "").slice(0, 5),
              roleKey(s.role),
            ].join("|");
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          const roleMissing = (day.missing_slots || []).filter((s) => roleKey(s.role) === roleName);
          const mergedMissing = mergeMissingSlotsByWindow(roleMissing);
          const roleExtra = (day.extra_slots || []).filter((s) => roleKey(s.role) === roleName);
          if (mergedOptimal.length === 0 && dedupedDraftSlots.length === 0 && mergedMissing.length === 0) return null;

          const coveredSlots = mergedOptimal.filter((opt) => {
            const optStart = toHour(opt.start);
            const optEnd = intervalEndHourFloat(opt.start, opt.end);
            const optDuration = optEnd - optStart;
            if (optDuration <= 0) return false;
            return dedupedDraftSlots.some((drf) => {
              if (roleKey(drf.role) !== roleKey(opt.role)) return false;
              const drfStart = toHour(drf.start);
              const drfEnd = intervalEndHourFloat(drf.start, drf.end);
              const overlap = Math.max(0, Math.min(drfEnd, optEnd) - Math.max(drfStart, optStart));
              return overlap / optDuration >= 0.5;
            });
          }).length;
          const totalOptimalSlots = mergedOptimal.length;
          const shouldDim = false;
          const utilisationColor = totalOptimalSlots > 0 && coveredSlots >= totalOptimalSlots
            ? "#16A34A"
            : coveredSlots === 0
              ? "#EF4444"
              : "#F97316";

          const sortedDraft = [...dedupedDraftSlots].sort((a, b) => toHour(a.start) - toHour(b.start));
          const conflictIndices = new Set();
          sortedDraft.forEach((a, i) => {
            const aStart = toHour(a.start);
            const aEnd = intervalEndHourFloat(a.start, a.end);
            const aName = String(a.employee_name || "").trim().toLowerCase();
            for (let j = i + 1; j < sortedDraft.length; j += 1) {
              const b = sortedDraft[j];
              const bName = String(b.employee_name || "").trim().toLowerCase();
              if (!aName || !bName || aName !== bName) continue;
              const bStart = toHour(b.start);
              const bEnd = intervalEndHourFloat(b.start, b.end);
              if (aStart < bEnd && bStart < aEnd) {
                conflictIndices.add(i);
                conflictIndices.add(j);
              }
            }
          });
          const draftLaneEnds = [];
          const draftedWithLane = sortedDraft.map((slot, idx) => {
            const geo = timelineBarGeometry(slot.start, slot.end, TIMELINE_START, TIMELINE_END);
            const sh = geo.startHour;
            const eh = geo.endHour;
            let lane = draftLaneEnds.findIndex((endVal) => sh >= endVal);
            if (lane === -1) {
              lane = draftLaneEnds.length;
              draftLaneEnds.push(eh);
            } else {
              draftLaneEnds[lane] = eh;
            }
            return { slot, lane, idx, isConflict: conflictIndices.has(idx) };
          });
          const draftLaneCount = Math.max(draftLaneEnds.length, 1);
          const sortedOptimalForLanes = [...mergedOptimal].sort((a, b) => toHour(a.start) - toHour(b.start));
          const laneIdealSlots = assignLanes(sortedOptimalForLanes);
          const idealLaneCount =
            laneIdealSlots.length === 0 ? 0 : Math.max(...laneIdealSlots.map((s) => s.laneIndex + 1), 1);
          const sortedMissingForLanes = [...mergedMissing].sort((a, b) => toHour(a.start) - toHour(b.start));
          const laneMissingSlots = assignLanes(sortedMissingForLanes);
          const missingLaneCount =
            laneMissingSlots.length === 0 ? 0 : Math.max(...laneMissingSlots.map((s) => s.laneIndex + 1), 1);
          const hasMissing = mergedMissing.length > 0;
          const idealSectionHeight = SHIFTIQ_ENABLED && idealLaneCount > 0 ? idealLaneCount * 22 + 4 : 0;
          const sepAfterIdeal = idealSectionHeight > 0 ? 1 : 0;
          const draftTopOffset = idealSectionHeight + sepAfterIdeal;
          const missingTopBase = draftTopOffset + draftLaneCount * 32;
          const containerHeight =
            idealSectionHeight +
            sepAfterIdeal +
            draftLaneCount * 32 +
            (missingLaneCount > 0 ? missingLaneCount * 24 + 8 : 0);
          const rowKey = `${day.date}-${roleName}`;
          const roleLabel = getRoleAbbr(roleName);
          const showTimelineLabels = roleName === "supervisor";
          const dayOpenClose = getOpenCloseForDay(day.day_name);
          const openHourRaw = toHour(dayOpenClose.open);
          let closeHourRaw = toHour(dayOpenClose.close);
          if (Number.isFinite(openHourRaw) && Number.isFinite(closeHourRaw) && closeHourRaw <= openHourRaw) {
            closeHourRaw += 24;
          }
          const openHour = clamp(openHourRaw, TIMELINE_START, TIMELINE_END);
          const closeHour = clamp(closeHourRaw, TIMELINE_START, TIMELINE_END);
          const openLeftPct = `${((openHour - TIMELINE_START) / TIMELINE_HOURS) * 100}%`;
          const closeLeftPct = `${((closeHour - TIMELINE_START) / TIMELINE_HOURS) * 100}%`;
          const beforeOpenWidthPct = `${Math.max(0, ((openHour - TIMELINE_START) / TIMELINE_HOURS) * 100)}%`;
          const afterCloseLeftPct = `${Math.max(0, ((closeHour - TIMELINE_START) / TIMELINE_HOURS) * 100)}%`;
          const afterCloseWidthPct = `${Math.max(0, ((TIMELINE_END - closeHour) / TIMELINE_HOURS) * 100)}%`;

          return (
            <div
              key={`${day.date}-${roleName}`}
              className={`gantt-role-row ${shouldDim ? "gantt-role-row-covered" : ""}`}
              style={{ display: "flex", alignItems: "flex-start", marginBottom: 6, opacity: shouldDim ? 0.55 : 1, transition: "opacity 0.2s ease" }}
            >
              <div style={{ width: 36, flexShrink: 0, paddingTop: 8, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                <div style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", color: "var(--text-muted)" }}>
                  {roleLabel}
                </div>
                {SHIFTIQ_ENABLED && totalOptimalSlots > 0 && (
                  <div style={{ fontSize: 8, color: utilisationColor, fontWeight: 700 }}>
                    {`${coveredSlots}/${totalOptimalSlots}`}
                  </div>
                )}
                {roleExtra.length > 0 && (
                  <div style={{ fontSize: 8, color: "#EF4444", fontWeight: 700 }}>
                    {`+${roleExtra.length} extra`}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, position: "relative", overflow: "visible" }}>
                <div
                  style={{ background: "var(--surface2)", borderRadius: 6, position: "relative", height: containerHeight, overflow: "visible" }}
                  ref={(el) => {
                    if (el) timelineWidthsRef.current[rowKey] = el.offsetWidth;
                  }}
                >
                  <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: beforeOpenWidthPct, background: "rgba(0,0,0,0.03)", pointerEvents: "none", zIndex: 0 }} />
                  <div style={{ position: "absolute", top: 0, bottom: 0, left: afterCloseLeftPct, width: afterCloseWidthPct, background: "rgba(0,0,0,0.03)", pointerEvents: "none", zIndex: 0 }} />
                  <div style={{ position: "absolute", top: 0, bottom: 0, left: openLeftPct, width: 1, background: "#10B98166", pointerEvents: "none", zIndex: 1 }} />
                  <div style={{ position: "absolute", top: 0, bottom: 0, left: closeLeftPct, width: 1, background: "rgba(138,158,148,0.4)", pointerEvents: "none", zIndex: 1 }} />
                  {showTimelineLabels && (
                    <>
                      <span style={{ position: "absolute", top: -14, left: openLeftPct, transform: "translateX(-50%)", fontSize: 8, color: "var(--green)", fontWeight: 700, whiteSpace: "nowrap", pointerEvents: "none", zIndex: 2 }}>
                        {dayOpenClose.open}
                      </span>
                      <span style={{ position: "absolute", top: -14, left: closeLeftPct, transform: "translateX(-50%)", fontSize: 8, color: "var(--text-muted)", fontWeight: 700, whiteSpace: "nowrap", pointerEvents: "none", zIndex: 2 }}>
                        {dayOpenClose.close}
                      </span>
                    </>
                  )}
                  {SHIFTIQ_ENABLED ? laneIdealSlots.map((slot, sidx) => {
                    const geo = getBarGeometry(slot);
                    if (!geo.visible) return null;
                    const optHours = hoursBetweenHhmm(slot.start, slot.end).toFixed(1);
                    const slotCount = slot.count || 1;
                    const highlightOptimal =
                      Boolean(dragPreview) && dragPreview.dayDate === day.date && roleKey(slot.role) === roleName;
                    return (
                      <div
                        key={`opt-${roleName}-${String(slot.start)}-${String(slot.end)}-${slot.laneIndex}-${sidx}`}
                        className="gantt-bar-item gantt-ideal-bar"
                        style={{
                          position: "absolute",
                          height: 16,
                          top: slot.laneIndex * 22 + 2,
                          left: geo.leftPct,
                          width: geo.widthPct,
                          background: statusColors.recommendedBg,
                          border: `${highlightOptimal ? 2.5 : 1.5}px dashed ${statusColors.recommendedBorder}`,
                          borderRadius: 4,
                          display: "flex",
                          alignItems: "center",
                          overflow: "visible",
                          whiteSpace: "nowrap",
                          paddingLeft: 4,
                          paddingRight: slotCount > 1 ? 36 : 4,
                          boxShadow: highlightOptimal ? `0 0 0 2px ${statusColors.recommendedBg}` : "none",
                          opacity: filteredDraftSlots.length === 0 || activeRole !== "all" ? 1 : undefined,
                        }}
                      >
                        <span style={{ fontSize: 8, fontWeight: 600, color: statusColors.recommendedText }}>
                          {`${getRoleAbbr(slot.role)} ${String(slot.start || "").slice(0, 2)}–${String(slot.end || "").slice(0, 2)}`}
                        </span>
                        {slotCount > 1 ? (
                          <span
                            style={{
                              position: "absolute",
                              right: 4,
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "rgba(0,0,0,0.25)",
                              color: "#fff",
                              fontSize: 8,
                              fontWeight: 800,
                              borderRadius: 999,
                              padding: "1px 5px",
                            }}
                          >
                            {`×${slotCount}`}
                          </span>
                        ) : null}
                        <span
                          className="gantt-tooltip"
                          style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap", pointerEvents: "none", opacity: 0, transition: "opacity 0.15s ease", zIndex: 200, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                        >
                          {`ShiftIQ optimal: ${roleKey(slot.role)} ${slot.start}–${slot.end} (${optHours}h) · Based on forecast demand`}
                        </span>
                      </div>
                    );
                  }) : null}
                  {SHIFTIQ_ENABLED && idealSectionHeight > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: idealSectionHeight,
                        height: 1,
                        background: "var(--border)",
                        zIndex: 3,
                        pointerEvents: "none",
                      }}
                    />
                  )}

                  {draftedWithLane.map(({ slot, lane, isConflict }, sidx) => {
                    const slotKey = `${day.date}-${roleName}-${slot.employee_name}-${slot.start}-${slot.end}-${sidx}`;
                    const previewForSlot = dragPreview && dragPreview.slotKey === slotKey ? dragPreview : null;
                    const slotForGeo = previewForSlot
                      ? { ...slot, start: toTimeLabel(previewForSlot.startHour), end: toTimeLabel(previewForSlot.endHour) }
                      : slot;
                    const geo = getBarGeometry(slotForGeo);
                    if (!geo.visible) return null;
                    const widthPctNum = parseFloat(geo.widthPct) || 0;
                    const rowWidth = timelineWidthsRef.current[rowKey] || 700;
                    const renderedPx = (widthPctNum / 100) * rowWidth;
                    const slotDisplayName = slotDraftDisplayName(slot, ganttStaffList);
                    const initials = getInitials(slotDisplayName);
                    const firstWord = employeeDisplayNameFirstWord(slotDisplayName, ganttStaffList);
                    const roleAbbrForSlot = getRoleAbbr(slot.role);
                    const draftEmpRole = (() => {
                      const n = String(slotDisplayName || "").toLowerCase().trim();
                      const hit = storeFilteredStaff.find(
                        (e) =>
                          String(e?.name || e?.employee_name || "")
                            .toLowerCase()
                            .trim() === n,
                      );
                      return hit?.group || hit?.role || "";
                    })();
                    const coveringSupervisor =
                      roleKey(slot.role) === "supervisor" && roleKey(draftEmpRole) === "service";
                    const roleAbbrDisplay = coveringSupervisor ? "Srv (sup)" : roleAbbrForSlot;
                    const startDisplay = String(slotForGeo.start || "").slice(0, 5);
                    const endDisplay = String(slotForGeo.end || "").slice(0, 5);
                    const shortTimes = `${startDisplay.slice(0, 2)}–${endDisplay.slice(0, 2)}`;
                    const fullTimes = `${startDisplay}–${endDisplay}`;
                    const draftHours = hoursBetweenHhmm(slotForGeo.start, slotForGeo.end).toFixed(1);
                    const conflictSuffix = isConflict ? " !" : "";
                    let barLabel = "";
                    if (renderedPx > 120 && firstWord) {
                      barLabel = `${fullTimes} · ${firstWord} · ${roleAbbrDisplay}${conflictSuffix}`;
                    } else if (renderedPx > 36 && firstWord) {
                      barLabel = `${roleAbbrDisplay} ${shortTimes} ${firstWord}${conflictSuffix}`;
                    } else if (widthPctNum > 20 && initials) {
                      barLabel = `${roleAbbrDisplay} ${shortTimes} ${initials}${conflictSuffix}`;
                    } else {
                      barLabel = `${roleAbbrDisplay} ${shortTimes}${conflictSuffix}`;
                    }
                    const draftTooltip = isConflict
                      ? `${slotDisplayName} is scheduled twice during this window — please resolve.`
                      : slot.drifts
                        ? `${slotDisplayName} · ${startDisplay}–${endDisplay} · ${draftHours}h · ⚠ Deviates from optimal`
                        : `${slotDisplayName} · ${startDisplay}–${endDisplay} · ${draftHours}h · Matches optimal`;
                    const isAutoDraft = String(slot.status || "").toLowerCase() === "auto_draft";
                    const isExtraSlot = roleExtra.some((ex) => isSameSlotWindow(ex, slot));
                    const deleteKey = deleteSlotKey(day.date, slot.role, slot.employee_name, slot.start);
                    const isDeleting = deletingSlot === deleteKey;
                    const hasDeleteError = deleteErrorSlot === deleteKey;
                    const cursorMode =
                      isDraggingDraft && dragStateRef.current?.slotKey === slotKey
                        ? "grabbing"
                        : dragHover.slotKey === slotKey && dragHover.mode !== "move"
                          ? "ew-resize"
                          : "grab";
                    return (
                      <div
                        key={`drf-${roleName}-${sidx}`}
                        className="gantt-bar-item"
                        onMouseMove={(e) => {
                          if (isDraggingDraft) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const localX = e.clientX - rect.left;
                          const edgePx = 8;
                          const mode =
                            localX <= edgePx ? "resize-left" : localX >= rect.width - edgePx ? "resize-right" : "move";
                          if (dragHover.slotKey !== slotKey || dragHover.mode !== mode) {
                            setDragHover({ slotKey, mode });
                          }
                        }}
                        onMouseLeave={() => {
                          if (!isDraggingDraft && dragHover.slotKey === slotKey) {
                            setDragHover({ slotKey: "", mode: "" });
                          }
                        }}
                        onMouseDown={(e) => {
                          if (scheduleReadOnly || e.button !== 0) return;
                          e.preventDefault();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const localX = e.clientX - rect.left;
                          const edgePx = 8;
                          const mode =
                            localX <= edgePx ? "resize-left" : localX >= rect.width - edgePx ? "resize-right" : "move";
                          const startHour = clamp(toHour(slot.start), TIMELINE_START, TIMELINE_END - 0.5);
                          const geoEnd = timelineBarGeometry(slot.start, slot.end, TIMELINE_START, TIMELINE_END).endHour;
                          const endHour = clamp(geoEnd, startHour + 0.5, TIMELINE_END);
                          dragStateRef.current = {
                            slotKey,
                            dayDate: day.date,
                            role: slot.role,
                            employeeName: slot.employee_name,
                            mode,
                            startHour,
                            endHour,
                            originX: e.clientX,
                            containerWidth: timelineWidthsRef.current[rowKey] || rect.width,
                          };
                          const previewStart = { slotKey, dayDate: day.date, role: slot.role, startHour, endHour };
                          dragPreviewRef.current = previewStart;
                          setDragPreview(previewStart);
                          setDragHover({ slotKey, mode });
                          setIsDraggingDraft(true);
                        }}
                        style={{
                          position: "absolute",
                          height: 24,
                          top: draftTopOffset + lane * 32 + 4,
                          left: geo.leftPct,
                          width: geo.widthPct,
                          background: statusColors.good,
                          color: "#fff",
                          borderLeft: slot.drifts ? "4px solid #F97316" : "none",
                          borderRadius: 6,
                          display: "flex",
                          alignItems: "center",
                          overflow: "visible",
                          whiteSpace: "nowrap",
                          paddingLeft: (slot.drifts ? 16 : 6) + (isAutoDraft ? 22 : 0),
                          zIndex: 2,
                          outline: isConflict || isExtraSlot ? "2px solid #EF4444" : "none",
                          outlineOffset: isConflict || isExtraSlot ? "1px" : 0,
                          cursor: cursorMode,
                          userSelect: "none",
                        }}
                      >
                        {isAutoDraft ? (
                          <span
                            style={{
                              position: "absolute",
                              top: 2,
                              left: 4,
                              fontSize: 7,
                              fontWeight: 900,
                              background: "rgba(255,255,255,0.3)",
                              color: "#fff",
                              borderRadius: 3,
                              padding: "0px 3px",
                              pointerEvents: "none",
                              zIndex: 4,
                            }}
                          >
                            AI
                          </span>
                        ) : null}
                        {isDeleting ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, paddingRight: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 800, color: "#fff" }}>Remove?</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmDeleteSlot(day.date, slot.role, slot.start, slot.end, slot.employee_name);
                              }}
                              style={{
                                border: "none",
                                borderRadius: 999,
                                background: "rgba(255,255,255,0.2)",
                                color: "#fff",
                                fontSize: 10,
                                fontWeight: 900,
                                padding: "2px 8px",
                                cursor: "pointer",
                              }}
                            >
                              Yes
                            </button>
                          </div>
                        ) : hasDeleteError ? (
                          <span style={{ fontSize: 10, fontWeight: 800, color: "#FEE2E2" }}>Failed to remove</span>
                        ) : (
                          <span style={{ fontSize: 10, fontWeight: 700 }}>
                            {barLabel}
                          </span>
                        )}
                        {slot.drifts && !isDeleting && !hasDeleteError ? (
                          <span
                            style={{
                              position: "absolute",
                              left: 6,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: 9,
                              color: "#fff",
                              fontWeight: 900,
                              pointerEvents: "none",
                            }}
                          >
                            ⚠
                          </span>
                        ) : null}
                        <span
                          className="gantt-tooltip"
                          style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap", pointerEvents: "none", opacity: 0, transition: "opacity 0.15s ease", zIndex: 200, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                        >
                          {draftTooltip}
                        </span>
                        {!scheduleReadOnly && !isDeleting && (
                          <button
                            type="button"
                            className="gantt-delete-btn"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSlot(day.date, slot.role, slot.start, slot.end, slot.employee_name);
                            }}
                            style={{
                              position: "absolute",
                              top: 3,
                              right: 4,
                              width: 16,
                              height: 16,
                              borderRadius: 999,
                              background: "rgba(0,0,0,0.25)",
                              color: "#fff",
                              fontSize: 10,
                              fontWeight: 900,
                              lineHeight: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "none",
                              cursor: "pointer",
                              opacity: 0,
                              transition: "opacity 0.15s",
                            }}
                          >
                            ✕
                          </button>
                        )}
                        <span
                          className="gantt-drag-tip"
                          style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#fff",
                            background: "rgba(26,26,26,0.9)",
                            borderRadius: 999,
                            padding: "3px 8px",
                            pointerEvents: "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t("Drag to adjust", "Σύρε για αλλαγή")}
                        </span>
                      </div>
                    );
                  })}

                  {hasMissing &&
                    laneMissingSlots.map((slot, sidx) => {
                      const geo = getBarGeometry(slot);
                      if (!geo.visible) return null;
                      const widthPctNum = parseFloat(geo.widthPct) || 0;
                      const perSlotHours = Number(slot.hours ?? hoursBetweenHhmm(slot.start, slot.end));
                      const missingCount =
                        slot.remaining_count != null && slot.remaining_count !== ""
                          ? Number(slot.remaining_count)
                          : Number(slot.count || 1);
                      const gapHoursNum =
                        slot.gap_hours != null && slot.gap_hours !== ""
                          ? Number(slot.gap_hours)
                          : perSlotHours * missingCount;
                      const gapHoursStr = Number.isFinite(gapHoursNum) ? gapHoursNum.toFixed(1) : String(perSlotHours * missingCount);
                      return (
                        <div
                          key={`mis-${roleName}-${String(slot.start)}-${String(slot.end)}-${slot.laneIndex}-${sidx}`}
                          className="gantt-missing-bar gantt-bar-item"
                          style={{
                            position: "absolute",
                            height: 18,
                            top: missingTopBase + slot.laneIndex * 24 + 2,
                            left: geo.leftPct,
                            width: geo.widthPct,
                            background: "transparent",
                            border: "1.5px dashed #EF4444",
                            borderRadius: 4,
                            display: "flex",
                            alignItems: "center",
                            overflow: "visible",
                            whiteSpace: "nowrap",
                            paddingLeft: 4,
                            paddingRight: missingCount > 1 ? 52 : 4,
                          }}
                        >
                          <span style={{ fontSize: 8, color: "#EF4444", fontWeight: 700 }}>
                            {`✕ ${getRoleAbbr(slot.role)} ${String(slot.start || "").slice(0, 2)}–${String(slot.end || "").slice(0, 2)}`}
                          </span>
                          {missingCount > 1 ? (
                            <span
                              style={{
                                position: "absolute",
                                right: 26,
                                top: "50%",
                                transform: "translateY(-50%)",
                                background: "rgba(0,0,0,0.25)",
                                color: "#fff",
                                fontSize: 8,
                                fontWeight: 800,
                                borderRadius: 999,
                                padding: "1px 5px",
                              }}
                            >
                              {`×${missingCount}`}
                            </span>
                          ) : null}
                          {!scheduleReadOnly ? (
                          <button
                            type="button"
                            className="gantt-add-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFillSlot(day.date, slot.role, slot.start, slot.end);
                            }}
                            style={{
                              position: "absolute",
                              right: 4,
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: 18,
                              height: 18,
                              borderRadius: 999,
                              background: "var(--green)",
                              color: "#fff",
                              fontSize: 12,
                              fontWeight: 900,
                              border: "none",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: widthPctNum > 8 ? 1 : 0,
                              transition: "opacity 0.15s",
                            }}
                          >
                            +
                          </button>
                          ) : null}
                          <span
                            className="gantt-tooltip"
                            style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap", pointerEvents: "none", opacity: 0, transition: "opacity 0.15s ease", zIndex: 200, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                          >
                            {`Gap: ${roleKey(slot.role)} ${slot.start}–${slot.end} · ${missingCount} person(s) still needed · ${gapHoursStr}h uncovered`}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          );
        })}

        <div style={{ position: "relative", marginLeft: 36, height: 14 }}>
          {[10, 12, 14, 16, 18, 20, 22].map((hour) => (
            <span
              key={`${day.date}-tick-${hour}`}
              style={{
                position: "absolute",
                left: `${((hour - TIMELINE_START) / TIMELINE_HOURS) * 100}%`,
                transform: "translateX(-50%)",
                fontSize: 9,
                color: "var(--text-muted)",
              }}
            >
              {hour}
            </span>
          ))}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") goPrevDay();
      if (e.key === "ArrowRight") goNextDay();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [days.length]);

  if (loading && !ganttData) {
    return (
      <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: 16, textAlign: "center" }}>
        <LoadingPulse size={20} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: 16, color: "var(--text-muted)", fontSize: 13 }}>
        {error}
      </div>
    );
  }

  if (!ganttData) return null;

  if (hideHeaderChrome) {
    const sidebarDay =
      (forcedDayDate ? days.find((d) => String(d?.date || "") === String(forcedDayDate)) : null) ||
      currentDay;
    if (!sidebarDay) {
      return (
        <div style={{ textAlign: "center", padding: 12 }}>
          <LoadingPulse size={18} />
        </div>
      );
    }
    return (
      <div style={{ background: "transparent" }}>
        {renderDayTimeline(sidebarDay, sidebarDay?.date || "sidebar-day")}
      </div>
    );
  }

  return (
    <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: 16 }}>
      <style>{`
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeSlidePrev {
          from { opacity: 0; transform: translateX(-12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .gantt-day-enter { animation: fadeSlide 0.2s ease; }
        .gantt-legend-item:hover .gantt-tooltip { opacity: 1 !important; }
        .gantt-bar-item:hover .gantt-tooltip { opacity: 1 !important; }
        .gantt-day-stripe-item:hover .gantt-tooltip { opacity: 1 !important; }
        .gantt-ideal-bar { opacity: 0; transition: opacity 0.15s ease; }
        .gantt-role-row:hover .gantt-ideal-bar { opacity: 1; }
        .gantt-bar-item:hover .gantt-delete-btn { opacity: 1 !important; }
        .gantt-bar-item .gantt-drag-tip { opacity: 0; transition: opacity 0.15s ease; }
        .gantt-bar-item:hover .gantt-drag-tip { opacity: 1 !important; }
        .gantt-bar-item:hover { z-index: 25 !important; }
        .gantt-role-row-covered:hover { opacity: 1 !important; }
        .gantt-missing-bar:hover .gantt-add-btn { opacity: 1 !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
      {loadingAction && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 9999, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 16
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: '32px 40px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <div style={{
              width: 40, height: 40, border: '4px solid #e5e7eb',
              borderTop: '4px solid #1a3c2e', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            <div style={{ fontWeight: 600, fontSize: 16, color: '#1a3c2e' }}>
              {loadingAction}
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', maxWidth: 260 }}>
              Please wait...
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text-primary)" }}>Schedule vs Optimal</div>
          <span style={{ marginLeft: 8 }}>
            {renderConfidenceBadge({
              confidence: weekConfidence,
              confidence_note:
                weekConfidence === "high"
                  ? "All days have sufficient historical data"
                  : weekConfidence === "medium"
                    ? "Some days have limited historical data"
                    : "One or more days have insufficient historical data",
            })}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div
            style={{
              background: "var(--surface2)",
              padding: 4,
              borderRadius: 10,
              display: "inline-flex",
              gap: 4,
            }}
          >
            {["day", "week"].map((view) => (
              <button
                key={view}
                type="button"
                onClick={() => setGanttView(view)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                  background: ganttView === view ? "var(--green)" : "transparent",
                  color: ganttView === view ? "#fff" : "var(--text-secondary)",
                }}
              >
                {view === "day" ? "Day" : "Week"}
              </button>
            ))}
          </div>
          <div style={{ position: "relative" }} onMouseDown={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowRoleMenu((p) => !p)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "5px 14px", borderRadius: 20,
                border: "1.5px solid var(--border, #e5e7eb)",
                fontSize: 13, fontWeight: 500,
                background: "var(--surface, #f9fafb)",
                color: "var(--text-secondary, #6b7280)",
                cursor: "pointer", outline: "none",
              }}
            >
              {roleOptions.find((r) => r.id === activeRole)?.label ?? "All roles"}
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1l4 4 4-4" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {showRoleMenu && (
              <div style={{
                position: "absolute", top: "110%", left: 0, zIndex: 300,
                background: "#fff", border: "1.5px solid var(--border, #e5e7eb)",
                borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                minWidth: 140, overflow: "hidden", padding: "4px 0",
              }}>
                {roleOptions.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => { setActiveRole(r.id); setShowRoleMenu(false); }}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      width: "100%", padding: "9px 14px",
                      background: activeRole === r.id ? "var(--surface, #f9fafb)" : "none",
                      border: "none", textAlign: "left", fontSize: 13,
                      fontWeight: activeRole === r.id ? 600 : 400,
                      color: activeRole === r.id ? "var(--green, #1a3c2e)" : "var(--text, #374151)",
                      cursor: "pointer",
                    }}
                  >
                    {r.label}
                    {activeRole === r.id && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="#1a3c2e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
        {!hasAnyDraftBars ? (
          <span style={{ fontSize: 14, color: 'var(--text-secondary, #6b7280)', fontWeight: 500 }}>
            No shifts assigned yet — click Apply optimal to fill the week.
          </span>
        ) : (
          <>
            <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 28, lineHeight: 1, color: healthScoreColor }}>
              {`${healthScore}% optimal`}
            </div>
          </>
        )}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn-base btn-green"
          onClick={applyOptimal}
          disabled={!!loadingAction}
          style={{ fontSize: 12, padding: "6px 14px", opacity: loadingAction ? 0.6 : 1, cursor: loadingAction ? 'not-allowed' : 'pointer' }}
        >
          {t("Apply optimal", "Εφαρμογή βέλτιστου")}
        </button>
        <div style={{ position: 'relative' }} onMouseDown={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setShowResetMenu((p) => !p)}
            disabled={!!loadingAction}
            style={{
              padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border, #e5e7eb)',
              background: 'var(--surface, #fff)', cursor: 'pointer', fontSize: 18,
              lineHeight: 1, color: 'var(--text, #1a3c2e)', fontWeight: 700,
              opacity: loadingAction ? 0.6 : 1,
            }}
          >
            ...
          </button>
          {showResetMenu && (
            <div style={{
              position: 'absolute', right: 0, top: '110%', background: '#fff',
              border: '1.5px solid var(--border, #e5e7eb)', borderRadius: 10,
              boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 200,
              minWidth: 180, overflow: 'hidden'
            }}>
              <button
                type="button"
                onClick={() => {
                  setShowResetMenu(false);
                  if (typeof onRequestResetAutoFills === "function") {
                    onRequestResetAutoFills();
                  } else {
                    resetOptimalWeek();
                  }
                }}
                disabled={!!loadingAction || scheduleReadOnly}
                style={{
                  display: 'block', width: '100%', padding: '11px 16px',
                  background: 'none', border: 'none', textAlign: 'left',
                  fontSize: 13, cursor: scheduleReadOnly ? 'not-allowed' : 'pointer',
                  color: scheduleReadOnly ? 'var(--text-muted)' : 'var(--text, #1a3c2e)',
                  opacity: scheduleReadOnly ? 0.5 : 1,
                }}
              >
                {t("Clear auto-fills only", "Αναίρεση μόνο αυτόματων πληρώσεων")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowResetMenu(false);
                  if (typeof onRequestResetWeek === "function") {
                    onRequestResetWeek();
                  } else {
                    clearWeek();
                  }
                }}
                disabled={!!loadingAction || scheduleReadOnly}
                style={{
                  display: 'block', width: '100%', padding: '11px 16px',
                  background: 'none', border: 'none', textAlign: 'left',
                  fontSize: 13, cursor: scheduleReadOnly ? 'not-allowed' : 'pointer',
                  color: scheduleReadOnly ? 'var(--text-muted)' : '#dc2626',
                  opacity: scheduleReadOnly ? 0.5 : 1,
                }}
              >
                {t(
                  "Reset entire week — clears all shifts",
                  "Επαναφορά εβδομάδας — διαγραφή όλων των βαρδιών",
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      {applyMsg ? (
        <div style={{ marginTop: -2, marginBottom: 8 }}>
          <span style={{ color: applyMsgColor, fontSize: 12, fontWeight: 700 }}>
            {applyMsg === "Optimal applied — gaps filled" ? t("Gaps filled", "Συμπληρώθηκαν κενά") : applyMsg}
          </span>
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
        {days.map((d, i) => {
          const gapHours = Number(d?.gap_hours || 0);
          const overHours = dayOverHours(d);
          const hasData = Boolean(d?.date);
          const dayHasDraftBars = (d?.draft_slots || []).length > 0;
          const stripeColor = !dayHasDraftBars
            ? "#e5e7eb"
            : !hasData
              ? "var(--border)"
              : gapHours > 0
                ? "#EF4444"
                : overHours > 0
                  ? "#F59E0B"
                  : "#16A34A";
          const dayLabel = String(d?.day_name || "").slice(0, 3);
          const dayDateLabel = d?.date
            ? new Date(`${d.date}T12:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
            : "";
          const dayTooltip = gapHours === 0 && overHours === 0
            ? `${dayLabel} ${dayDateLabel} · On track`
            : `${dayLabel} ${dayDateLabel} · -${gapHours.toFixed(0)}h uncovered · +${overHours.toFixed(0)}h over`;
          return (
            <div
              key={`health-day-${d.date || i}`}
              className="gantt-day-stripe-item"
              style={{ flex: 1, minWidth: 0, position: "relative", cursor: ganttView === "week" ? "pointer" : "default" }}
              onClick={() => scrollToWeekDayBlock(d, i)}
            >
              <div style={{ width: "100%", height: 6, borderRadius: 3, background: stripeColor }} />
              <span
                className="gantt-tooltip"
                style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap", pointerEvents: "none", opacity: 0, transition: "opacity 0.15s ease", zIndex: 200, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
              >
                {dayTooltip}
              </span>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 9,
                  color: "var(--text-muted)",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <span>{dayLabel}</span>
                {renderExceptionalDayBadge(d)}
              </div>
            </div>
          );
        })}
      </div>
      {showDragCoachmark && (
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span>{t("Tip: drag bars to move, drag edges to resize.", "Tip: σύρε τις μπάρες για μετακίνηση, τις άκρες για αλλαγή ώρας.")}</span>
          <button
            type="button"
            onClick={showDragTipsAgain}
            style={{
              border: "none",
              background: "transparent",
              color: "var(--green-mid)",
              textDecoration: "underline",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              padding: 0,
              lineHeight: 1.2,
            }}
          >
            {t("Show tips again", "Εμφάνιση tips ξανά")}
          </button>
        </span>
      </div>
      )}
      {lastDragAudit ? (
        <div style={{ marginTop: -6, marginBottom: 10 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "var(--surface2)",
              border: "1px solid var(--border)",
              borderRadius: 999,
              padding: "4px 10px",
              fontSize: 11,
              fontWeight: 700,
              color: "var(--text-secondary)",
            }}
          >
            {`${t("Last drag save", "Τελευταία αποθήκευση drag")} ${toClockLabel(lastDragAudit.at)} · ${lastDragAudit.employeeName} · ${lastDragAudit.start}–${lastDragAudit.end}`}
          </span>
        </div>
      ) : null}
      {showDragCoachmark && hasAnyDraftBars && (
        <div
          style={{
            marginBottom: 12,
            border: "1.5px solid var(--green-mid)",
            background: "var(--green-light)",
            borderRadius: 10,
            padding: "10px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 12, color: "var(--text-primary)", fontWeight: 700 }}>
            {t("New: adjust draft shifts directly on the chart. Drag center to move, or drag bar edges to resize.", "Νέο: ρύθμισε βάρδιες απευθείας στο γράφημα. Σύρε το κέντρο για μετακίνηση ή τις άκρες για αλλαγή διάρκειας.")}
          </span>
          <button
            type="button"
            onClick={dismissDragCoachmark}
            style={{
              border: "1.5px solid var(--border)",
              background: "var(--surface)",
              color: "var(--text-secondary)",
              borderRadius: 8,
              padding: "5px 10px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {t("Got it", "Κατάλαβα")}
          </button>
        </div>
      )}

      {hasAnyDraftBars ? (
      <div style={{ marginBottom: 10, fontSize: 12, fontWeight: 800, color: "var(--text-secondary)" }}>
        {t("Action now: fix red (uncovered), then orange (needs adjustment).", "Ενέργεια τώρα: διόρθωσε πρώτα τα κόκκινα (ακάλυπτα), μετά τα πορτοκαλί (χρειάζονται προσαρμογή).")}
      </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: showGanttLegend ? 6 : 16 }}>
        <button
          type="button"
          onClick={() => setShowGanttLegend((p) => !p)}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '3px 10px', borderRadius: 20,
            border: '1.5px solid var(--border, #e5e7eb)',
            background: 'var(--surface, #f9fafb)',
            fontSize: 11, fontWeight: 600,
            color: 'var(--text-muted, #9ca3af)',
            cursor: 'pointer'
          }}
        >
          <span style={{
            width: 14, height: 14, borderRadius: '50%',
            border: '1.5px solid var(--border, #d1d5db)',
            display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 9, fontWeight: 700,
            color: 'var(--text-muted, #9ca3af)'
          }}>?</span>
          Legend {showGanttLegend ? '▲' : '▼'}
        </button>
      </div>
      {showGanttLegend && (
      <div style={{ display: "flex", gap: 16, marginBottom: 16, fontSize: 11, color: "var(--text-muted)", fontWeight: 600, flexWrap: "wrap" }}>
        <div className="gantt-legend-item" style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width="12" height="8"><rect x="0.75" y="0.75" width="10.5" height="6.5" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="2 2" /></svg>
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 4 }}>
            <span>{t("Recommended", "Προτεινόμενο")}</span>
            <span style={{ fontSize: 9, color: "var(--text-muted)" }}>{t("(hover to show)", "(hover για εμφάνιση)")}</span>
          </span>
          <span
            className="gantt-tooltip"
            style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap", pointerEvents: "none", opacity: 0, transition: "opacity 0.15s ease", zIndex: 200, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
          >
            The shift pattern ShiftIQ recommends for this role and time window based on forecast demand
          </span>
        </div>
        <div className="gantt-legend-item" style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width="12" height="8"><rect x="0" y="0" width="12" height="8" fill="#3B82F6" /></svg>
          {t("Good (assigned)", "Καλό (ανατεθειμένο)")}
          <span
            className="gantt-tooltip"
            style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap", pointerEvents: "none", opacity: 0, transition: "opacity 0.15s ease", zIndex: 200, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
          >
            A shift you have assigned that matches the optimal plan
          </span>
        </div>
        <div className="gantt-legend-item" style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width="12" height="8">
            <rect x="0" y="0" width="12" height="8" fill="#3B82F6" />
            <rect x="0" y="0" width="3.5" height="8" fill="#F97316" />
          </svg>
          {t("Needs adjustment", "Χρειάζεται προσαρμογή")}
          <span
            className="gantt-tooltip"
            style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap", pointerEvents: "none", opacity: 0, transition: "opacity 0.15s ease", zIndex: 200, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
          >
            A shift you have assigned that deviates from the optimal — wrong time window or overstaffed
          </span>
        </div>
        <div className="gantt-legend-item" style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width="12" height="8"><rect x="0.75" y="0.75" width="10.5" height="6.5" fill="#3B82F6" stroke="#EF4444" strokeWidth="1.5" /></svg>
          {t("Over optimal", "Πάνω από βέλτιστο")}
          <span
            className="gantt-tooltip"
            style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap", pointerEvents: "none", opacity: 0, transition: "opacity 0.15s ease", zIndex: 200, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
          >
            Assigned shift that exceeds optimal demand coverage (extra staffing)
          </span>
        </div>
        <div className="gantt-legend-item" style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width="12" height="8"><rect x="0.75" y="0.75" width="10.5" height="6.5" fill="none" stroke="#EF4444" strokeWidth="1.5" strokeDasharray="2 2" /></svg>
          {t("Uncovered", "Ακάλυπτο")}
          <span
            className="gantt-tooltip"
            style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap", pointerEvents: "none", opacity: 0, transition: "opacity 0.15s ease", zIndex: 200, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
          >
            An optimal slot with no draft coverage — this gap may affect service levels
          </span>
        </div>
      </div>
      )}

      {ganttView === "day" && currentDay && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
            <button
              onClick={goPrevDay}
              disabled={dayIndex === 0}
              style={{
                border: "1.5px solid var(--border)",
                background: "var(--surface2)",
                borderRadius: 8,
                width: 32,
                height: 32,
                fontSize: 16,
                fontWeight: 700,
                cursor: dayIndex === 0 ? "default" : "pointer",
                color: "var(--text-primary)",
                opacity: dayIndex === 0 ? 0.3 : 1,
              }}
            >
              {"<"}
            </button>
            <div style={{ display: "flex", flexDirection: "column", textAlign: "center", alignItems: "center" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontSize: 15, fontWeight: 800, color: "var(--text-primary)" }}>
                  {currentDay.day_name}
                </span>
                {renderConfidenceBadge(currentDay)}
                {renderExceptionalDayBadge(currentDay)}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                {currentDay.date
                  ? new Date(`${currentDay.date}T12:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
                  : ""}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {Number(currentDay.gap_hours || 0) > 0 || dayOverHours(currentDay) > 0 ? (
                <>
                  {Number(currentDay.gap_hours || 0) > 0 ? (
                  <span style={{ background: "#FEF3C7", color: "#92400E", fontSize: 10, fontWeight: 800, borderRadius: 999, padding: "2px 8px" }}>
                    {`−${Number(currentDay.gap_hours || 0).toFixed(0)}h`}
                  </span>
                  ) : null}
                  {dayOverHours(currentDay) > 0 ? (
                    <span style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 10, fontWeight: 800, borderRadius: 999, padding: "2px 8px" }}>
                      {`+${dayOverHours(currentDay).toFixed(0)}h`}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => applyDayOptimal(currentDay)}
                    disabled={!!loadingAction}
                    style={{
                      border: "1.5px solid var(--green)",
                      background: "transparent",
                      color: "var(--green)",
                      fontSize: 11,
                      fontWeight: 700,
                      borderRadius: 8,
                      padding: "3px 10px",
                      cursor: loadingAction ? 'not-allowed' : "pointer",
                      opacity: loadingAction ? 0.6 : 1,
                    }}
                  >
                    {t("Fill gaps", "Συμπλήρωση κενών")}
                  </button>
                </>
              ) : (
                <span style={{ background: "#DCFCE7", color: "#16A34A", fontSize: 10, fontWeight: 800, borderRadius: 999, padding: "2px 8px" }}>
                  {t("On track", "Εντός στόχου")}
                </span>
              )}
              <button
                type="button"
                onClick={() => resetOptimalDay(currentDay)}
                disabled={!!loadingAction}
                title={t("Clear all shifts for this day (cannot be undone)", "Διαγραφή όλων των βαρδιών για την ημέρα (μη αναστρέψιμο)")}
                style={{
                  border: "1.5px solid var(--border)",
                  background: "var(--surface2)",
                  color: "var(--text-secondary)",
                  fontSize: 10,
                  fontWeight: 700,
                  borderRadius: 8,
                  padding: "3px 10px",
                  cursor: loadingAction ? 'not-allowed' : "pointer",
                  opacity: loadingAction ? 0.6 : 1,
                }}
              >
                {t("Clear day", "Καθαρισμός ημέρας")}
              </button>
              <button
                onClick={goNextDay}
                disabled={dayIndex === days.length - 1}
                style={{
                  border: "1.5px solid var(--border)",
                  background: "var(--surface2)",
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: dayIndex === days.length - 1 ? "default" : "pointer",
                  color: "var(--text-primary)",
                  opacity: dayIndex === days.length - 1 ? 0.3 : 1,
                }}
              >
                {">"}
              </button>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16 }}>
            {days.map((d, i) => {
              const isActive = i === dayIndex;
              const hasGap = Number(d?.gap_hours || 0) > 0;
              return (
                <button
                  key={`dot-${d.date || i}`}
                  onClick={() => {
                    lastDirectionRef.current = i < dayIndex ? "prev" : "next";
                    setDayIndex(i);
                  }}
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    background: isActive ? "var(--green)" : hasGap ? "#F9731699" : "var(--border)",
                  }}
                />
              );
            })}
          </div>

          <div
            key={dayIndex}
            className="gantt-day-enter"
            style={{ animation: `${lastDirectionRef.current === "prev" ? "fadeSlidePrev" : "fadeSlide"} 0.2s ease` }}
          >
            {renderDayTimeline(currentDay, currentDay.date || dayIndex)}
          </div>
        </>
      )}
      {ganttView === "week" && (
        <div ref={weekScrollContainerRef} style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {days.map((day, idx) => (
            <div
              key={day.date || `week-${idx}`}
              ref={(el) => {
                weekDayBlockRefs.current[String(day?.date || idx)] = el;
                dayBlockRefs.current[String(day?.date || idx)] = el;
              }}
              style={{ borderBottom: "1.5px solid var(--border)", paddingBottom: 14, marginBottom: 8 }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: "var(--text-primary)" }}>{day.day_name}</span>
                    {renderConfidenceBadge(day)}
                    {renderExceptionalDayBadge(day)}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {day.date
                      ? new Date(`${day.date}T12:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
                      : ""}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {Number(day.gap_hours || 0) > 0 || dayOverHours(day) > 0 ? (
                    <>
                      {Number(day.gap_hours || 0) > 0 ? (
                      <span style={{ background: "#FEF3C7", color: "#92400E", fontSize: 10, fontWeight: 800, borderRadius: 999, padding: "2px 8px" }}>
                        {`−${Number(day.gap_hours || 0).toFixed(0)}h`}
                      </span>
                      ) : null}
                      {dayOverHours(day) > 0 ? (
                        <span style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 10, fontWeight: 800, borderRadius: 999, padding: "2px 8px" }}>
                          {`+${dayOverHours(day).toFixed(0)}h`}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => applyDayOptimal(day)}
                        disabled={!!loadingAction}
                        style={{
                          border: "1.5px solid var(--green)",
                          background: "transparent",
                          color: "var(--green)",
                          fontSize: 11,
                          fontWeight: 700,
                          borderRadius: 8,
                          padding: "3px 10px",
                          cursor: loadingAction ? 'not-allowed' : "pointer",
                          opacity: loadingAction ? 0.6 : 1,
                        }}
                      >
                        {t("Fill gaps", "Συμπλήρωση κενών")}
                      </button>
                    </>
                  ) : (
                    <span style={{ background: "#DCFCE7", color: "#16A34A", fontSize: 10, fontWeight: 800, borderRadius: 999, padding: "2px 8px" }}>
                      {t("On track", "Εντός στόχου")}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => resetOptimalDay(day)}
                    disabled={!!loadingAction}
                    title={t("Clear all shifts for this day (cannot be undone)", "Διαγραφή όλων των βαρδιών για την ημέρα (μη αναστρέψιμο)")}
                    style={{
                      border: "1.5px solid var(--border)",
                      background: "var(--surface2)",
                      color: "var(--text-secondary)",
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: 8,
                      padding: "3px 10px",
                      cursor: loadingAction ? 'not-allowed' : "pointer",
                      opacity: loadingAction ? 0.6 : 1,
                    }}
                  >
                    {t("Clear day", "Καθαρισμός ημέρας")}
                  </button>
                </div>
              </div>
              {renderDayTimeline(day, day.date || `timeline-${idx}`)}
            </div>
          ))}
        </div>
      )}
      {showFillSlotModal && fillSlotModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "var(--surface)", borderRadius: 14, padding: 24, width: 340, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text-primary)", marginBottom: 4 }}>Fill gap</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>
              {`${fillSlotModal.role} · ${fillSlotModal.start}–${fillSlotModal.end}`}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12 }}>
              {fillGapAvailabilitySummary}
            </div>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1.5px solid var(--border)", fontSize: 13, fontWeight: 600, color: "var(--text-primary)", background: "var(--surface2)", marginBottom: 16 }}
            >
              <option value="" disabled>Select employee...</option>
              {primaryAvailable.length > 0 && (
                <optgroup label={`${gapRoleTitleForSummary} crew`}>
                  {primaryAvailable.map((opt) => (
                    <option key={`primary-${opt.name}`} value={opt.name}>
                      {`${opt.label} (${opt.scheduledDays}/${opt.contractedDays})${fillGapAvailableOptionSuffix}`}
                    </option>
                  ))}
                </optgroup>
              )}
              {secondaryAvailable.length > 0 && (
                <optgroup label="Other roles (cross-cover)">
                  {secondaryAvailable.map((opt) => {
                    const srcRole = getEmployeeSourceRole(opt.name);
                    const rkSrc = roleKey(srcRole);
                    const roleLabel =
                      rkSrc === "store_manager" || rkSrc === "store manager"
                        ? t("Store manager", "Διευθυντής καταστήματος")
                        : srcRole
                          ? roleTitle(srcRole)
                          : t("Crew", "Προσωπικό");
                    const unk = opt.storeUnknown ? t(" (store unknown)", " (άγνωστο κατάστημα)") : "";
                    return (
                      <option
                        key={`secondary-${opt.name}`}
                        value={opt.name}
                        style={{ color: "var(--text-muted)", fontWeight: 500 }}
                      >
                        {`${opt.name}${unk} · ${roleLabel} (${opt.scheduledDays}/${opt.contractedDays})${fillGapAvailableOptionSuffix}`}
                      </option>
                    );
                  })}
                </optgroup>
              )}
              {onLeaveEmployeeOptions.length > 0 && (
                <optgroup label="On approved leave">
                  {onLeaveEmployeeOptions.map((opt) => (
                    <option
                      key={`leave-${opt.name}`}
                      value={opt.name}
                      disabled
                      style={{ color: "var(--text-muted)" }}
                    >
                      {`${opt.label} (${opt.scheduledDays}/${opt.contractedDays})`}
                    </option>
                  ))}
                </optgroup>
              )}
              {alreadyScheduledEmployeeOptions.length > 0 && (
                <optgroup label="Already working today">
                  {alreadyScheduledEmployeeOptions.map((opt) => (
                    <option
                      key={`scheduled-${opt.name}`}
                      value={opt.name}
                      disabled
                      style={{ color: "var(--text-muted)" }}
                    >
                      {`${opt.label} (${opt.scheduledDays}/${opt.contractedDays})`}
                    </option>
                  ))}
                </optgroup>
              )}
              {!adminScheduleOverride &&
                (restViolationEmployeeOptions.length > 0 || maxDaysReachedEmployeeOptions.length > 0) && (
                <optgroup label="Rest violation / Max days">
                  {restViolationEmployeeOptions.map((opt) => (
                    <option
                      key={`rest-${opt.name}`}
                      value={opt.name}
                      disabled
                      style={{ color: "var(--text-muted)" }}
                    >
                      {`${opt.label} (${opt.scheduledDays}/${opt.contractedDays}) — only ${Math.floor((opt.restViolation?.gap || 0) / 60)}h rest`}
                    </option>
                  ))}
                  {maxDaysReachedEmployeeOptions.map((opt) => (
                    <option
                      key={`maxed-${opt.name}`}
                      value={opt.name}
                      disabled
                      style={{ color: "var(--text-muted)" }}
                    >
                      {`${opt.label} (${opt.scheduledDays}/${opt.contractedDays})`}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {fillGapBlockWarning && (
              <div style={{ color: "#B91C1C", fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
                {fillGapBlockWarning}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={closeFillModal}
                style={{ border: "1.5px solid var(--border)", background: "transparent", color: "var(--text-secondary)", borderRadius: 8, padding: "7px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                className="btn-base btn-green"
                onClick={fillGapCanAssign ? assignFillSlot : undefined}
                disabled={!selectedEmployee || !fillGapCanAssign}
                style={{ opacity: !selectedEmployee || !fillGapCanAssign ? 0.4 : 1, cursor: !selectedEmployee || !fillGapCanAssign ? "not-allowed" : "pointer" }}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


